/**
 * Balance Sheet - CFO/Board-Level Executive Dashboard
 * Redesigned for executive steering and financial structure analysis
 * Design: Quiet Luxury Corporativo - Angolan Night theme
 */

import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, Building, Building2, Wallet, PieChart, Layers,
  ArrowLeftRight, Clock, RefreshCw, Info, Shield, AlertTriangle,
  Target, CheckCircle, CreditCard, Banknote, Scale, Activity, Droplets, FileText,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, ComposedChart, Line, LabelList
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import ExecutiveDrilldownCard from '../../components/ExecutiveDrilldownCard';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getShortBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

// Import REAL Balance Sheet data for 2026 Budget
import { 
  realBalanceSheetData, 
  toMillionsUSD 
} from '../../data/mock/realFinancialData';

// Utility
const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

// Premium KPI Tooltip Component - Compact Version (50% smaller)
const KPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
  if (!isVisible) return null;
  
  return (
    <div 
      className="absolute z-50 w-56 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 bottom-full mb-2 left-1/2 -translate-x-1/2"
      style={{ 
        boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)',
      }}
    >
      {/* Arrow */}
      <div 
        className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -bottom-1 border-b border-r"
      />
      
      {/* Content - Compact */}
      <div className="space-y-2">
        {/* KPI Title */}
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        
        {/* Definition */}
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definição</p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        
        {/* Relevance */}
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevância</p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        
        {/* Current Reading */}
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
      </div>
    </div>
  );
};

// ============ SEMANTIC COLOR SYSTEM - SCORECARD PATTERN ============
const statusColors = {
  positive: '#0C8A4B',
  positiveBg: '#EAF7EF',
  warning: '#A07A18',
  warningBg: '#F8F1DD',
  negative: '#C33D52',
  negativeBg: '#FBECEF',
};

const getSemanticBadgeStyle = (val, inverse = false, useWarning = true) => {
  if (val === undefined || val === null) return { bg: 'bg-slate-100', text: 'text-slate-600' };
  const isFavorable = inverse ? val <= 0 : val >= 0;
  const absVal = Math.abs(val);
  if (useWarning && !isFavorable && absVal <= 5) {
    return { bg: 'bg-amber-50', text: 'text-amber-700', bgColor: statusColors.warningBg, color: statusColors.warning };
  }
  if (isFavorable) {
    return { bg: 'bg-emerald-100', text: 'text-emerald-700', bgColor: statusColors.positiveBg, color: statusColors.positive };
  }
  return { bg: 'bg-rose-100', text: 'text-rose-700', bgColor: statusColors.negativeBg, color: statusColors.negative };
};

const getTrendIconInfo = (vsBudget, vsYoY, inverse = false) => {
  const mainVal = vsBudget !== undefined ? vsBudget : vsYoY;
  if (mainVal === undefined) return null;
  const isFavorable = inverse ? mainVal <= 0 : mainVal >= 0;
  const isUp = mainVal > 0;
  return {
    isUp,
    bgColor: isFavorable ? statusColors.positiveBg : statusColors.negativeBg,
    iconColor: isFavorable ? statusColors.positive : statusColors.negative,
  };
};

// Enhanced Metric Card with Premium Tooltip - SCORECARD EXECUTIVO PATTERN
const MetricCardWithTooltip = ({ 
  title, value, unit, vsBudget, vsYoY, changeUnit = '%', icon: Icon, color = 'primary', subtitle, inverse = false,
  tooltipData, useWarning = true
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Get semantic styles
  const budgetStyle = getSemanticBadgeStyle(vsBudget, inverse, useWarning);
  const yoyStyle = getSemanticBadgeStyle(vsYoY, inverse, useWarning);
  const trendInfo = getTrendIconInfo(vsBudget, vsYoY, inverse);

  return (
    <div 
      className="relative bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-gold-300/50 transition-all duration-300 cursor-help overflow-visible"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        {/* Left side: Title, Value, Badges */}
        <div className="flex-1 min-w-0">
          {/* Title - Normal case, not uppercase */}
          <p className="text-xs text-slate-500 tracking-wide mb-1.5">{title}</p>
          
          {/* Value + Unit - Smaller size to match Personnel Costs */}
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-2xl font-semibold tracking-[-0.3px] text-primary">
              {value}
            </span>
            <span className="text-sm text-slate-400">{unit}</span>
          </div>
          
          {subtitle && <p className="text-xs text-slate-400 mb-2">{subtitle}</p>}
          
          {/* Comparison Badges - Inline compact style with no wrap */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {vsBudget !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${budgetStyle.bg} ${budgetStyle.text}`}>
                {vsBudget >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {vsBudget > 0 ? '+' : ''}{vsBudget}{changeUnit} vs Orç.
              </span>
            )}
            {vsYoY !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${yoyStyle.bg} ${yoyStyle.text}`}>
                {vsYoY >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {vsYoY > 0 ? '+' : ''}{vsYoY}{changeUnit} YoY
              </span>
            )}
          </div>
        </div>
        
        {/* Right side: Circular Trend Icon - SCORECARD PATTERN */}
        {trendInfo && (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendInfo.bgColor }}
          >
            {trendInfo.isUp ? (
              <ArrowUpRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            ) : (
              <ArrowDownRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            )}
          </div>
        )}
        
        {/* Info indicator if no trend */}
        {!trendInfo && (
          <Info className="w-4 h-4 text-slate-300 hover:text-gold-500 transition-colors flex-shrink-0 ml-2" />
        )}
      </div>
      
      {/* Premium Compact Tooltip */}
      {tooltipData && (
        <KPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
    </div>
  );
};

// Enhanced Metric Card (legacy, without tooltip) - SCORECARD PATTERN
const MetricCard = ({ title, value, unit, vsBudget, vsYoY, changeUnit = '%', icon: Icon, color = 'primary', subtitle, inverse = false, useWarning = true }) => {
  // Get semantic styles
  const budgetStyle = getSemanticBadgeStyle(vsBudget, inverse, useWarning);
  const yoyStyle = getSemanticBadgeStyle(vsYoY, inverse, useWarning);
  const trendInfo = getTrendIconInfo(vsBudget, vsYoY, inverse);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-gold-300/50 transition-all duration-300">
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        {/* Left side: Title, Value, Badges */}
        <div className="flex-1 min-w-0">
          {/* Title - Normal case, not uppercase */}
          <p className="text-xs text-slate-500 tracking-wide mb-1.5">{title}</p>
          
          {/* Value + Unit - Smaller size to match Personnel Costs */}
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-2xl font-semibold tracking-[-0.3px] text-primary">
              {value}
            </span>
            <span className="text-sm text-slate-400">{unit}</span>
          </div>
          
          {subtitle && <p className="text-xs text-slate-400 mb-2">{subtitle}</p>}
          
          {/* Comparison Badges - Inline compact style with no wrap */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {vsBudget !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${budgetStyle.bg} ${budgetStyle.text}`}>
                {vsBudget >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {vsBudget > 0 ? '+' : ''}{vsBudget}{changeUnit} vs Orç.
              </span>
            )}
            {vsYoY !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${yoyStyle.bg} ${yoyStyle.text}`}>
                {vsYoY >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {vsYoY > 0 ? '+' : ''}{vsYoY}{changeUnit} YoY
              </span>
            )}
          </div>
        </div>
        
        {/* Right side: Circular Trend Icon - SCORECARD PATTERN */}
        {trendInfo && (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendInfo.bgColor }}
          >
            {trendInfo.isUp ? (
              <ArrowUpRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            ) : (
              <ArrowDownRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Mini KPI Card for sections
const MiniKPICard = ({ title, value, unit, change, changeLabel, isPositive, icon: Icon, color = 'slate' }) => {
  const colorStyles = {
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
    gold: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
    slate: { bg: 'bg-slate-50', icon: 'text-slate-600', border: 'border-slate-200' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', border: 'border-red-200' }
  };
  const style = colorStyles[color] || colorStyles.slate;
  
  return (
    <div className={`${style.bg} rounded-lg p-4 border ${style.border}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-4 h-4 ${style.icon}`} />}
        <span className="text-xs font-medium text-slate-500">{title}</span>
      </div>
      <p className="font-serif text-2xl font-semibold text-slate-800">
        {value}<span className="text-sm text-slate-400 ml-1">{unit}</span>
      </p>
      {change !== undefined && (
        <p className={`text-xs mt-1 ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
          {change > 0 ? '+' : ''}{change} {changeLabel}
        </p>
      )}
    </div>
  );
};

// Alert Card for Risks section
const AlertCard = ({ title, description, severity, icon: Icon }) => {
  const severityStyles = {
    high: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-500', title: 'text-red-700' },
    medium: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-500', title: 'text-amber-700' },
    low: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500', title: 'text-blue-700' }
  };
  const style = severityStyles[severity] || severityStyles.medium;
  
  return (
    <div className={`${style.bg} border ${style.border} rounded-xl p-4`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg}`}>
          <Icon className={`w-4 h-4 ${style.icon}`} />
        </div>
        <div>
          <h4 className={`font-semibold text-sm ${style.title}`}>{title}</h4>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
};

const BalanceSheet = ({ year, language, setActiveSection, flowMap }) => {
  // Get data from mock
  const balanceData = mockData.balanceSheet[year] || mockData.balanceSheet[2025];
  const prevBalanceData = mockData.balanceSheet[year - 1] || mockData.balanceSheet[2024];
  const workingCapitalData = mockData.workingCapital[year] || mockData.workingCapital[2025];
  const prevWorkingCapitalData = mockData.workingCapital[year - 1] || mockData.workingCapital[2024];
  const investedCapitalData = mockData.investedCapital[year] || mockData.investedCapital[2025];
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const budgetLabelShort = getShortBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  const vsOrcLabel = year === 2026 ? 'vs Orç. 1T' : 'vs Orç.';
  const yoyLabel = year === 2026 ? `vs ${previousPeriod}` : 'YoY';
  
  // ============ REAL BUDGET DATA ============
  // For each year: use REAL data from realBalanceSheetData for the budget column
  // 2025 view → uses realBalanceSheetData[2025] for "Orçamento 2025"
  // 2026 view → uses realBalanceSheetData[2026] for "Orçamento 2026"
  const budgetRealData = realBalanceSheetData[year] || realBalanceSheetData[2026];
  
  // ============ REAL 2024 DATA ============
  // Use REAL data from realBalanceSheetData[2024] for the "2024" column
  const prev2024RealData = realBalanceSheetData[2024];
  
  // Previous year values from REAL Excel data - converted to Millions USD
  const realPrevYearData = prev2024RealData ? {
    // ACTIVO
    tangibleAssets: toMillionsUSD(prev2024RealData.imobilizacoesCorporeas),
    intangibleAssets: toMillionsUSD(prev2024RealData.imobilizacoesIncorporeas),
    fixedAssets: toMillionsUSD(prev2024RealData.totalActivosNaoCorrentes),
    inventory: toMillionsUSD(prev2024RealData.existencias),
    receivables: toMillionsUSD(prev2024RealData.contasAReceber),
    cash: toMillionsUSD(prev2024RealData.disponibilidades),
    otherCurrentAssets: toMillionsUSD(prev2024RealData.outrosActivosCorrentes),
    currentAssets: toMillionsUSD(prev2024RealData.totalActivosCorrentes),
    totalAssets: toMillionsUSD(prev2024RealData.totalActivo),
    // CAPITAL PROPRIO
    capital: toMillionsUSD(prev2024RealData.capital),
    reserves: toMillionsUSD(prev2024RealData.reservas),
    retainedEarnings: toMillionsUSD(prev2024RealData.resultadosTransitados),
    netIncome: toMillionsUSD(prev2024RealData.resultadosExercicio),
    otherEquity: toMillionsUSD(prev2024RealData.outrosInstrumentosCapitalProprio),
    totalEquity: toMillionsUSD(prev2024RealData.totalCapitalProprio),
    // PASSIVO NAO CORRENTE
    longTermDebt: toMillionsUSD(prev2024RealData.emprestimosMedioLongoPrazo),
    provisions: toMillionsUSD(prev2024RealData.provisoesOutrosRiscosEncargos),
    otherNonCurrentLiab: toMillionsUSD(prev2024RealData.outrosPassivosNaoCorrentes),
    totalNonCurrentLiab: toMillionsUSD(prev2024RealData.totalPassivoNaoCorrente),
    // PASSIVO CORRENTE
    shortTermPortion: toMillionsUSD(prev2024RealData.parteCorrenteEmprestimos),
    payables: toMillionsUSD(prev2024RealData.contasAPagar),
    shortTermLoans: toMillionsUSD(prev2024RealData.emprestimosCurtoPrazo),
    otherCurrentLiab: toMillionsUSD(prev2024RealData.outrosPassivosCorrentes),
    totalCurrentLiab: toMillionsUSD(prev2024RealData.totalPassivoCorrente),
    // TOTALS
    totalCapitalProprioEPassivo: toMillionsUSD(prev2024RealData.totalCapitalProprioEPassivo),
  } : null;
  
  // Budget values from REAL Excel data - converted to Millions USD
  const budgetBalanceData = {
    // ACTIVO
    tangibleAssets: toMillionsUSD(budgetRealData.imobilizacoesCorporeas),
    intangibleAssets: toMillionsUSD(budgetRealData.imobilizacoesIncorporeas),
    fixedAssets: toMillionsUSD(budgetRealData.totalActivosNaoCorrentes),
    inventory: toMillionsUSD(budgetRealData.existencias),
    receivables: toMillionsUSD(budgetRealData.contasAReceber),
    cash: toMillionsUSD(budgetRealData.disponibilidades),
    otherCurrentAssets: toMillionsUSD(budgetRealData.outrosActivosCorrentes),
    currentAssets: toMillionsUSD(budgetRealData.totalActivosCorrentes),
    totalAssets: toMillionsUSD(budgetRealData.totalActivo),
    // CAPITAL PROPRIO
    capital: toMillionsUSD(budgetRealData.capital),
    reserves: toMillionsUSD(budgetRealData.reservas),
    retainedEarnings: toMillionsUSD(budgetRealData.resultadosTransitados),
    netIncome: toMillionsUSD(budgetRealData.resultadosExercicio),
    otherEquity: toMillionsUSD(budgetRealData.outrosInstrumentosCapitalProprio),
    totalEquity: toMillionsUSD(budgetRealData.totalCapitalProprio),
    // PASSIVO NAO CORRENTE
    longTermDebt: toMillionsUSD(budgetRealData.emprestimosMedioLongoPrazo),
    provisions: toMillionsUSD(budgetRealData.provisoesOutrosRiscosEncargos),
    otherNonCurrentLiab: toMillionsUSD(budgetRealData.outrosPassivosNaoCorrentes),
    totalNonCurrentLiab: toMillionsUSD(budgetRealData.totalPassivoNaoCorrente),
    // PASSIVO CORRENTE
    shortTermPortion: toMillionsUSD(budgetRealData.parteCorrenteEmprestimos),
    payables: toMillionsUSD(budgetRealData.contasAPagar),
    shortTermLoans: toMillionsUSD(budgetRealData.emprestimosCurtoPrazo),
    otherCurrentLiab: toMillionsUSD(budgetRealData.outrosPassivosCorrentes),
    totalCurrentLiab: toMillionsUSD(budgetRealData.totalPassivoCorrente),
    // TOTALS
    totalCapitalProprioEPassivo: toMillionsUSD(budgetRealData.totalCapitalProprioEPassivo),
    // RATIOS
    financialAutonomy: (budgetRealData.totalCapitalProprio / budgetRealData.totalActivo * 100),
  };
  
  // Budget totals for KPI cards
  const budgetTotalAssets = budgetBalanceData.totalAssets;
  const budgetEquity = budgetBalanceData.totalEquity;
  const budgetAutonomy = budgetBalanceData.financialAutonomy;
  const budgetWorkingCapital = budgetBalanceData.currentAssets - budgetBalanceData.totalCurrentLiab;
  const budgetInvestedCapital = budgetBalanceData.totalEquity + budgetBalanceData.longTermDebt + budgetBalanceData.shortTermPortion - budgetBalanceData.cash;
  
  // Calculate vs Budget
  const assetsVsBudget = budgetTotalAssets !== 0 ? ((balanceData.totalAssets - budgetTotalAssets) / Math.abs(budgetTotalAssets) * 100).toFixed(1) : '0';
  const equityVsBudget = budgetEquity !== 0 ? ((balanceData.totalEquity - budgetEquity) / Math.abs(budgetEquity) * 100).toFixed(1) : '0';
  const autonomyVsBudget = (balanceData.financialAutonomy - budgetAutonomy).toFixed(1);
  const wcVsBudget = budgetWorkingCapital !== 0 ? ((workingCapitalData.netWorkingCapital - budgetWorkingCapital) / Math.abs(budgetWorkingCapital) * 100).toFixed(1) : '0';
  const icVsBudget = budgetInvestedCapital !== 0 ? ((investedCapitalData.totalInvestedCapital - budgetInvestedCapital) / Math.abs(budgetInvestedCapital) * 100).toFixed(1) : '0';
  
  // Calculate YoY
  const assetsYoY = ((balanceData.totalAssets / prevBalanceData.totalAssets - 1) * 100).toFixed(1);
  const equityYoY = ((balanceData.totalEquity / prevBalanceData.totalEquity - 1) * 100).toFixed(1);
  const autonomyYoY = (balanceData.financialAutonomy - prevBalanceData.financialAutonomy).toFixed(1);
  const wcYoY = ((workingCapitalData.netWorkingCapital - prevWorkingCapitalData.netWorkingCapital) / Math.abs(prevWorkingCapitalData.netWorkingCapital) * 100).toFixed(1);

  // Calculated values
  const totalLiabilities = balanceData.totalAssets - balanceData.totalEquity;
  const prevTotalLiabilities = prevBalanceData.totalAssets - prevBalanceData.totalEquity;
  const currentLiabilities = balanceData.shortTermDebt + balanceData.suppliers + (balanceData.otherLiabilities * 0.3);
  const prevCurrentLiabilities = prevBalanceData.shortTermDebt + prevBalanceData.suppliers + (prevBalanceData.otherLiabilities * 0.3);
  const nonCurrentLiabilities = totalLiabilities - currentLiabilities;
  const prevNonCurrentLiabilities = prevTotalLiabilities - prevCurrentLiabilities;
  
  // Ratios
  const currentRatio = (balanceData.currentAssets / currentLiabilities).toFixed(2);
  const prevCurrentRatio = (prevBalanceData.currentAssets / prevCurrentLiabilities).toFixed(2);
  const quickRatio = ((balanceData.currentAssets - balanceData.inventory) / currentLiabilities).toFixed(2);
  const prevQuickRatio = ((prevBalanceData.currentAssets - prevBalanceData.inventory) / prevCurrentLiabilities).toFixed(2);
  const fixedAssetsRatio = ((balanceData.fixedAssets / balanceData.totalAssets) * 100).toFixed(1);
  const currentLiabRatio = ((currentLiabilities / totalLiabilities) * 100).toFixed(1);
  
  // Debt metrics
  const grossDebt = balanceData.shortTermDebt + balanceData.longTermDebt;
  const prevGrossDebt = prevBalanceData.shortTermDebt + prevBalanceData.longTermDebt;
  const netDebt = grossDebt - balanceData.cash;
  const prevNetDebt = prevGrossDebt - prevBalanceData.cash;
  const cashCoverage = ((balanceData.cash / balanceData.shortTermDebt) * 100).toFixed(0);
  
  // EBITDA for leverage (using approximate from revenue)
  const ebitda = 102; // From mock data
  const netDebtEbitda = (netDebt / ebitda).toFixed(1);

  // Key Insights - CAPEX style
  const balanceInsights = {
    pt: [
      { title: 'ESTRUTURA', description: `Autonomia financeira de ${balanceData.financialAutonomy}% (+${autonomyYoY}p.p. YoY). Mais de metade do activo financiado por capitais proprios.`, color: 'text-emerald-400', icon: Shield },
      { title: 'LIQUIDEZ', description: `Current Ratio de ${currentRatio}x e Fundo de Maneio de ${formatM(workingCapitalData.netWorkingCapital)}M USD. Posicao corrente confortavel.`, color: 'text-gold-400', icon: Wallet },
      { title: 'ALAVANCAGEM', description: `Divida Liquida / EBITDA de ${netDebtEbitda}x. Estrutura de financiamento sustentavel com margem de flexibilidade.`, color: 'text-blue-400', icon: Scale },
    ],
    en: [
      { title: 'STRUCTURE', description: `Financial autonomy of ${balanceData.financialAutonomy}% (+${autonomyYoY}p.p. YoY). More than half of assets financed by equity.`, color: 'text-emerald-400', icon: Shield },
      { title: 'LIQUIDITY', description: `Current Ratio of ${currentRatio}x and Working Capital of ${formatM(workingCapitalData.netWorkingCapital)}M USD. Comfortable current position.`, color: 'text-gold-400', icon: Wallet },
      { title: 'LEVERAGE', description: `Net Debt / EBITDA of ${netDebtEbitda}x. Sustainable financing structure with flexibility margin.`, color: 'text-blue-400', icon: Scale },
    ],
  };

  // Budget values for structure charts - USE REAL DATA from 2026 Budget
  const budgetCurrentAssets = budgetBalanceData.currentAssets;
  const budgetFixedAssets = budgetBalanceData.fixedAssets;
  const budgetCurrentLiabilities = budgetBalanceData.totalCurrentLiab;
  const budgetNonCurrentLiabilities = budgetBalanceData.totalNonCurrentLiab;
  const budgetEquityValue = budgetBalanceData.totalEquity;

  // Structure comparison data (horizontal stacked bars) - with Budget
  const structureComparisonData = [
    {
      name: year.toString(),
      'Activo Corrente': balanceData.currentAssets,
      'Activo Nao Corrente': balanceData.fixedAssets,
      total: balanceData.totalAssets,
    },
    {
      name: `Orc. ${year}`,
      'Activo Corrente': budgetCurrentAssets,
      'Activo Nao Corrente': budgetFixedAssets,
      total: budgetCurrentAssets + budgetFixedAssets,
    },
    {
      name: (year - 1).toString(),
      'Activo Corrente': prevBalanceData.currentAssets,
      'Activo Nao Corrente': prevBalanceData.fixedAssets,
      total: prevBalanceData.totalAssets,
    },
  ];

  const financingComparisonData = [
    {
      name: year.toString(),
      'Passivo Corrente': currentLiabilities,
      'Passivo Nao Corrente': nonCurrentLiabilities,
      'Capital Proprio': balanceData.totalEquity,
      total: balanceData.totalAssets,
    },
    {
      name: `Orc. ${year}`,
      'Passivo Corrente': budgetCurrentLiabilities,
      'Passivo Nao Corrente': budgetNonCurrentLiabilities,
      'Capital Proprio': budgetEquityValue,
      total: budgetCurrentLiabilities + budgetNonCurrentLiabilities + budgetEquityValue,
    },
    {
      name: (year - 1).toString(),
      'Passivo Corrente': prevCurrentLiabilities,
      'Passivo Nao Corrente': prevNonCurrentLiabilities,
      'Capital Proprio': prevBalanceData.totalEquity,
      total: prevBalanceData.totalAssets,
    },
  ];

  // Budget values for liquidity ratios
  const budgetCurrentRatio = 1.60;
  const budgetQuickRatio = 1.15;

  // Budget and prev values for Structure KPIs
  const budgetAutonomyFinanceira = 54.0;
  const prevAutonomyFinanceira = prevBalanceData.financialAutonomy;
  const budgetFixedAssetsRatio = 65.0;
  const prevFixedAssetsRatio = ((prevBalanceData.fixedAssets / prevBalanceData.totalAssets) * 100).toFixed(1);
  const budgetCurrentLiabRatio = 46.0;
  const prevCurrentLiabRatio = ((prevCurrentLiabilities / prevTotalLiabilities) * 100).toFixed(1);

  // Working Capital Waterfall - TRUE CASCADE/WATERFALL
  // Use consistent data from balanceSheet for all values
  // FM = Activo Corrente - Passivo Corrente = (receivables + inventory + cash + other) - (suppliers + shortTermDebt + other)
  // Simplified: FM ~ receivables + inventory - suppliers (main drivers)
  
  // Calculate FM from components for consistency
  const fmStart2024 = prevBalanceData.receivables + prevBalanceData.inventory - prevBalanceData.suppliers;
  const fmEnd2025 = balanceData.receivables + balanceData.inventory - balanceData.suppliers;
  
  // Use mock FM values if available, otherwise calculated
  const fmStart = prevWorkingCapitalData.netWorkingCapital; // 80.9M from workingCapital
  const fmEnd = workingCapitalData.netWorkingCapital; // 72.0M from workingCapital
  
  // Calculate actual changes in each component
  const clientesChange = balanceData.receivables - prevBalanceData.receivables; // 68.2 - 78.5 = -10.3 (reduced)
  const existenciasChange = balanceData.inventory - prevBalanceData.inventory;  // 52.4 - 45.2 = +7.2 (increased)  
  const fornecedoresChange = balanceData.suppliers - prevBalanceData.suppliers; // 48.6 - 42.8 = +5.8 (increased)
  
  // Economic impact on FM (Working Capital):
  // - Decrease in receivables = POSITIVE (frees cash, improves FM)
  // - Increase in receivables = NEGATIVE (ties cash, worsens FM)
  // - Decrease in inventory = POSITIVE (frees cash, improves FM)
  // - Increase in inventory = NEGATIVE (ties cash, worsens FM)
  // - Increase in payables = POSITIVE (delays outflow, improves FM... wait, but FM = Current Assets - Current Liabs)
  // Actually for FM formula: FM = Receivables + Inventory - Payables
  // So: increase in payables = DECREASES FM (negative impact on FM number, but positive for cash!)
  
  // Let's recalculate correctly based on FM = R + I - P
  // Impact on FM = ΔR + ΔI - ΔP
  // clientesImpact = ΔR (if receivables decrease, FM decreases too, but cash improves)
  // existenciasImpact = ΔI (if inventory increases, FM increases too, but cash worsens)
  // fornecedoresImpact = -ΔP (if payables increase, FM decreases, but cash improves)
  
  // For CFO perspective on CASH, we need to flip the logic:
  // Cash impact from receivables: -ΔR (decrease in R = cash inflow = positive for cash)
  // Cash impact from inventory: -ΔI (decrease in I = cash freed = positive for cash)
  // Cash impact from payables: +ΔP (increase in P = cash delayed = positive for cash)
  
  // The FM changed from 80.9 to 72.0 = -8.9M
  // Using components: fmStart2024 = 78.5 + 45.2 - 42.8 = 80.9 ✓
  // fmEnd2025 = 68.2 + 52.4 - 48.6 = 72.0 ✓
  
  // Impact calculation (positive = FM increases, negative = FM decreases):
  const clientesImpact = clientesChange;      // -10.3 (receivables down = FM down)
  const existenciasImpact = existenciasChange; // +7.2 (inventory up = FM up)
  const fornecedoresImpact = -fornecedoresChange; // -5.8 (payables up = FM down)
  
  // Verify: -10.3 + 7.2 - 5.8 = -8.9 ✓ (matches 80.9 - 72.0)
  
  // Build cumulative positions for waterfall cascade effect
  const cumul1 = fmStart; // 80.9 (after FM 2024)
  const cumul2 = cumul1 + clientesImpact; // 80.9 - 10.3 = 70.6 (after Clientes)
  const cumul3 = cumul2 + existenciasImpact; // 70.6 + 7.2 = 77.8 (after Existências)
  const cumul4 = cumul3 + fornecedoresImpact; // 77.8 - 5.8 = 72.0 (after Fornecedores = FM 2025) ✓
  
  // Determine color based on whether impact improves or worsens FM
  // For CFO reading: green = good for FM (increases it), red = bad for FM (decreases it)
  // But from CASH perspective: decrease in FM can be good (cash freed from working capital)
  // Let's use: green = increases FM, red = decreases FM (pure accounting view)
  
  // Waterfall data for TRUE cascade visualization
  const wcWaterfallData = [
    { 
      name: `FM ${year - 1}`, 
      base: 0,
      value: fmStart,
      displayValue: fmStart,
      fill: '#0A192F',
      type: 'anchor'
    },
    { 
      name: language === 'pt' ? 'Clientes' : 'Receivables', 
      base: Math.min(cumul1, cumul2),
      value: Math.abs(clientesImpact),
      displayValue: clientesImpact,
      fill: clientesImpact >= 0 ? '#10B981' : '#EF4444',
      type: 'delta',
      cumulative: cumul2
    },
    { 
      name: language === 'pt' ? 'Existencias' : 'Inventory', 
      base: Math.min(cumul2, cumul3),
      value: Math.abs(existenciasImpact),
      displayValue: existenciasImpact,
      fill: existenciasImpact >= 0 ? '#10B981' : '#EF4444',
      type: 'delta',
      cumulative: cumul3
    },
    { 
      name: language === 'pt' ? 'Fornecedores' : 'Suppliers', 
      base: Math.min(cumul3, cumul4),
      value: Math.abs(fornecedoresImpact),
      displayValue: fornecedoresImpact,
      fill: fornecedoresImpact >= 0 ? '#10B981' : '#EF4444',
      type: 'delta',
      cumulative: cumul4
    },
    { 
      name: `FM ${year}`, 
      base: 0,
      value: fmEnd,
      displayValue: fmEnd,
      fill: '#0A192F',
      type: 'anchor'
    },
  ];

  // Generate executive summary text for waterfall
  const wcChangeDirection = fmEnd >= fmStart ? 'aumentou' : 'reduziu-se';
  const wcChangeDirectionEn = fmEnd >= fmStart ? 'increased' : 'decreased';
  
  // Build dynamic summary based on actual impacts
  // Clientes: decrease in receivables = FM decreases but cash improves (collection)
  const clientesSummaryPt = clientesImpact < 0 
    ? 'pressionado pela reducao de clientes (cobranca mais rapida)' 
    : 'suportado pelo aumento de clientes';
  const existenciasSummaryPt = existenciasImpact > 0 
    ? 'parcialmente compensado pelo aumento de existencias' 
    : 'e reducao de existencias';
  const fornecedoresSummaryPt = fornecedoresImpact < 0 
    ? 'e maior financiamento de fornecedores' 
    : 'agravado pela reducao do credito de fornecedores';
  
  const wcSummaryPt = `O Fundo de Maneio ${wcChangeDirection} de ${formatM(fmStart)}M para ${formatM(fmEnd)}M (${fmEnd >= fmStart ? '+' : ''}${formatM(fmEnd - fmStart)}M), ${clientesSummaryPt}, ${existenciasSummaryPt} ${fornecedoresSummaryPt}.`;
  
  const clientesSummaryEn = clientesImpact < 0 
    ? 'pressured by receivables reduction (faster collection)' 
    : 'supported by receivables increase';
  const existenciasSummaryEn = existenciasImpact > 0 
    ? 'partially offset by inventory increase' 
    : 'and inventory reduction';
  const fornecedoresSummaryEn = fornecedoresImpact < 0 
    ? 'and higher supplier financing' 
    : 'worsened by reduced supplier credit';
  
  const wcSummaryEn = `Working Capital ${wcChangeDirectionEn} from ${formatM(fmStart)}M to ${formatM(fmEnd)}M (${fmEnd >= fmStart ? '+' : ''}${formatM(fmEnd - fmStart)}M), ${clientesSummaryEn}, ${existenciasSummaryEn} ${fornecedoresSummaryEn}.`;

  // CCC Decomposition
  const cccData = [
    { name: 'DSO', value: workingCapitalData.dso, fill: '#3B82F6' },
    { name: 'DIO', value: workingCapitalData.dio, fill: '#C5A059' },
    { name: 'DPO', value: -workingCapitalData.dpo, fill: '#10B981' },
  ];

  // Budget values for debt metrics - USE REAL DATA from 2026 Budget
  const budgetGrossDebt = budgetBalanceData.longTermDebt + budgetBalanceData.shortTermPortion + budgetBalanceData.shortTermLoans;
  const budgetNetDebt = budgetGrossDebt - budgetBalanceData.cash;
  const budgetCash = budgetBalanceData.cash;
  const budgetNetDebtEbitda = 1.2;

  // Debt Structure - with Budget (using REAL budget data)
  const debtStructureData = [
    {
      name: year.toString(),
      'Curto Prazo': balanceData.shortTermDebt,
      'Longo Prazo': balanceData.longTermDebt,
      total: grossDebt,
    },
    {
      name: `Orc. ${year}`,
      'Curto Prazo': budgetBalanceData.shortTermPortion + budgetBalanceData.shortTermLoans,
      'Longo Prazo': budgetBalanceData.longTermDebt,
      total: budgetGrossDebt,
    },
    {
      name: (year - 1).toString(),
      'Curto Prazo': prevBalanceData.shortTermDebt,
      'Longo Prazo': prevBalanceData.longTermDebt,
      total: prevGrossDebt,
    },
  ];

  // Balance Sheet Rows for table
  const currentLongTermDebt = balanceData.longTermDebt;
  // Use REAL 2024 data for previous year values
  const prevLongTermDebt = realPrevYearData ? realPrevYearData.longTermDebt : prevBalanceData.longTermDebt;
  const currentProvisions = balanceData.otherLiabilities * 0.4;
  // Use REAL 2024 data for provisions
  const prevProvisions = realPrevYearData ? realPrevYearData.provisions : (prevBalanceData.otherLiabilities * 0.4);
  const currentOtherNonCurrentLiab = balanceData.otherLiabilities * 0.3;
  // Use REAL 2024 data for other non-current liabilities
  const prevOtherNonCurrentLiab = realPrevYearData ? realPrevYearData.otherNonCurrentLiab : (prevBalanceData.otherLiabilities * 0.3);
  const currentTotalNonCurrentLiab = currentLongTermDebt + currentProvisions + currentOtherNonCurrentLiab;
  const prevTotalNonCurrentLiab = realPrevYearData ? realPrevYearData.totalNonCurrentLiab : (prevLongTermDebt + prevProvisions + prevOtherNonCurrentLiab);
  
  const currentShortTermPortion = balanceData.shortTermDebt * 0.3;
  // Use REAL 2024 data for short-term portion
  const prevShortTermPortion = realPrevYearData ? realPrevYearData.shortTermPortion : (prevBalanceData.shortTermDebt * 0.3);
  const currentPayables = balanceData.suppliers;
  const prevPayables = realPrevYearData ? realPrevYearData.payables : prevBalanceData.suppliers;
  const currentShortTermLoans = balanceData.shortTermDebt * 0.7;
  const prevShortTermLoans = realPrevYearData ? realPrevYearData.shortTermLoans : (prevBalanceData.shortTermDebt * 0.7);
  const currentOtherCurrentLiab = balanceData.otherLiabilities * 0.3;
  // Use REAL 2024 data for other current liabilities
  const prevOtherCurrentLiab = realPrevYearData ? realPrevYearData.otherCurrentLiab : (prevBalanceData.otherLiabilities * 0.3);
  const currentTotalCurrentLiab = currentShortTermPortion + currentPayables + currentShortTermLoans + currentOtherCurrentLiab;
  const prevTotalCurrentLiab = realPrevYearData ? realPrevYearData.totalCurrentLiab : (prevShortTermPortion + prevPayables + prevShortTermLoans + prevOtherCurrentLiab);

  const currentCapital = balanceData.shareCapital;
  const prevCapital = realPrevYearData ? realPrevYearData.capital : prevBalanceData.shareCapital;
  const currentReserves = balanceData.reserves;
  const prevReserves = realPrevYearData ? realPrevYearData.reserves : prevBalanceData.reserves;
  
  // ============ 2025 REAL VALUES ============
  // Use REAL budget data for 2025 retained earnings and net income (from Excel)
  // budgetBalanceData.retainedEarnings = 22.8 M USD (from realFinancialData.js)
  // budgetBalanceData.netIncome = 8.7 M USD (from realFinancialData.js)
  // budgetBalanceData.otherEquity = 0 M USD (from realFinancialData.js)
  const currentRetainedEarnings = budgetBalanceData.retainedEarnings;
  // Use REAL 2024 data for retained earnings
  const prevRetainedEarnings = realPrevYearData ? realPrevYearData.retainedEarnings : (prevBalanceData.retainedEarnings * 0.6);
  const currentNetIncome = budgetBalanceData.netIncome;
  // Use REAL 2024 data for net income
  const prevNetIncome = realPrevYearData ? realPrevYearData.netIncome : (prevBalanceData.retainedEarnings * 0.4);
  // Use REAL budget data for other equity (0 for 2025)
  const currentOtherEquity = budgetBalanceData.otherEquity;
  // Use REAL 2024 data for other equity instruments
  const prevOtherEquity = realPrevYearData ? realPrevYearData.otherEquity : (prevBalanceData.totalEquity - prevCapital - prevReserves - prevRetainedEarnings - prevNetIncome);
  
  // Previous year total equity for table (from REAL data)
  const prevTotalEquity = realPrevYearData ? realPrevYearData.totalEquity : prevBalanceData.totalEquity;

  const balanceSheetRows = [
    { type: 'main-header', label: { pt: 'ACTIVO', en: 'ASSETS' } },
    { type: 'sub-header', label: { pt: 'Activos nao Correntes', en: 'Non-Current Assets' } },
    { type: 'row', label: { pt: 'Imobilizacoes Corporeas', en: 'Property, Plant & Equipment' }, current: balanceData.tangibleAssets, budget: budgetBalanceData.tangibleAssets, prev: prevBalanceData.tangibleAssets, indent: true },
    { type: 'row', label: { pt: 'Imobilizacoes Incorporeas', en: 'Intangible Assets' }, current: balanceData.intangibleAssets, budget: budgetBalanceData.intangibleAssets, prev: prevBalanceData.intangibleAssets, indent: true },
    { type: 'subtotal', label: { pt: 'Total Activos nao Correntes', en: 'Total Non-Current Assets' }, current: balanceData.fixedAssets, budget: budgetBalanceData.fixedAssets, prev: prevBalanceData.fixedAssets },
    { type: 'sub-header', label: { pt: 'Activos Correntes', en: 'Current Assets' } },
    { type: 'row', label: { pt: 'Existencias', en: 'Inventories' }, current: balanceData.inventory, budget: budgetBalanceData.inventory, prev: prevBalanceData.inventory, indent: true },
    { type: 'row', label: { pt: 'Contas a Receber', en: 'Receivables' }, current: balanceData.receivables, budget: budgetBalanceData.receivables, prev: prevBalanceData.receivables, indent: true },
    { type: 'row', label: { pt: 'Disponibilidades', en: 'Cash & Equivalents' }, current: balanceData.cash, budget: budgetBalanceData.cash, prev: prevBalanceData.cash, indent: true },
    { type: 'row', label: { pt: 'Outros Activos Correntes', en: 'Other Current Assets' }, current: balanceData.otherCurrentAssets, budget: budgetBalanceData.otherCurrentAssets, prev: prevBalanceData.otherCurrentAssets, indent: true },
    { type: 'subtotal', label: { pt: 'Total Activos Correntes', en: 'Total Current Assets' }, current: balanceData.currentAssets, budget: budgetBalanceData.currentAssets, prev: prevBalanceData.currentAssets },
    { type: 'grand-total', label: { pt: 'Total do Activo', en: 'Total Assets' }, current: balanceData.totalAssets, budget: budgetBalanceData.totalAssets, prev: prevBalanceData.totalAssets },
    { type: 'main-header', label: { pt: 'CAPITAL PROPRIO E PASSIVO', en: 'EQUITY & LIABILITIES' } },
    { type: 'sub-header', label: { pt: 'Capital Proprio', en: 'Equity' } },
    { type: 'row', label: { pt: 'Capital', en: 'Share Capital' }, current: currentCapital, budget: budgetBalanceData.capital, prev: prevCapital, indent: true },
    { type: 'row', label: { pt: 'Reservas', en: 'Reserves' }, current: currentReserves, budget: budgetBalanceData.reserves, prev: prevReserves, indent: true },
    { type: 'row', label: { pt: 'Resultados Transitados', en: 'Retained Earnings' }, current: currentRetainedEarnings, budget: budgetBalanceData.retainedEarnings, prev: prevRetainedEarnings, indent: true },
    { type: 'row', label: { pt: 'Resultados do Exercicio', en: 'Net Income' }, current: currentNetIncome, budget: budgetBalanceData.netIncome, prev: prevNetIncome, indent: true },
    { type: 'row', label: { pt: 'Outros Instrumentos de Capital Proprio', en: 'Other Equity Instruments' }, current: currentOtherEquity, budget: budgetBalanceData.otherEquity, prev: prevOtherEquity, indent: true },
    { type: 'subtotal', label: { pt: 'Total Capital Proprio', en: 'Total Equity' }, current: balanceData.totalEquity, budget: budgetBalanceData.totalEquity, prev: prevTotalEquity },
    { type: 'sub-header', label: { pt: 'Passivo nao Corrente', en: 'Non-Current Liabilities' } },
    { type: 'row', label: { pt: 'Emprestimos de Medio e Longo Prazos', en: 'Long-Term Loans' }, current: currentLongTermDebt, budget: budgetBalanceData.longTermDebt, prev: prevLongTermDebt, indent: true },
    { type: 'row', label: { pt: 'Provisoes para Outros Riscos e Encargos', en: 'Provisions' }, current: currentProvisions, budget: budgetBalanceData.provisions, prev: prevProvisions, indent: true },
    { type: 'row', label: { pt: 'Outros Passivos nao Correntes', en: 'Other Non-Current Liabilities' }, current: currentOtherNonCurrentLiab, budget: budgetBalanceData.otherNonCurrentLiab, prev: prevOtherNonCurrentLiab, indent: true },
    { type: 'subtotal', label: { pt: 'Total Passivo nao Corrente', en: 'Total Non-Current Liabilities' }, current: currentTotalNonCurrentLiab, budget: budgetBalanceData.totalNonCurrentLiab, prev: prevTotalNonCurrentLiab },
    { type: 'sub-header', label: { pt: 'Passivo Corrente', en: 'Current Liabilities' } },
    { type: 'row', label: { pt: 'Parte Cor. dos Empr. a Medio e Longo Prazos', en: 'Current Portion of Long-Term Debt' }, current: currentShortTermPortion, budget: budgetBalanceData.shortTermPortion, prev: prevShortTermPortion, indent: true },
    { type: 'row', label: { pt: 'Contas a Pagar', en: 'Payables' }, current: currentPayables, budget: budgetBalanceData.payables, prev: prevPayables, indent: true },
    { type: 'row', label: { pt: 'Emprestimos de Curto Prazo', en: 'Short-Term Loans' }, current: currentShortTermLoans, budget: budgetBalanceData.shortTermLoans, prev: prevShortTermLoans, indent: true },
    { type: 'row', label: { pt: 'Outros Passivos Correntes', en: 'Other Current Liabilities' }, current: currentOtherCurrentLiab, budget: budgetBalanceData.otherCurrentLiab, prev: prevOtherCurrentLiab, indent: true },
    { type: 'subtotal', label: { pt: 'Total Passivo Corrente', en: 'Total Current Liabilities' }, current: currentTotalCurrentLiab, budget: budgetBalanceData.totalCurrentLiab, prev: prevTotalCurrentLiab },
    { type: 'grand-total', label: { pt: 'Total do Capital Proprio e Passivo', en: 'Total Equity & Liabilities' }, current: balanceData.totalAssets, budget: budgetBalanceData.totalCapitalProprioEPassivo, prev: realPrevYearData ? realPrevYearData.totalCapitalProprioEPassivo : prevBalanceData.totalAssets },
  ];

  // Risks and Alerts
  const balanceAlerts = {
    pt: [
      { title: 'Liquidez', description: `Current Ratio de ${currentRatio}x acima do threshold de 1.5x. Posicao corrente confortavel, sem pressao imediata de refinanciamento.`, severity: 'low', icon: CheckCircle },
      { title: 'Working Capital', description: `Clientes com DSO de ${workingCapitalData.dso} dias representa a principal fonte de capital empatado. Monitorizar cobranca.`, severity: 'medium', icon: Clock },
      { title: 'Estrutura', description: `Peso do imobilizado de ${fixedAssetsRatio}% no activo indica intensidade de capital tipica de operacao mineira.`, severity: 'low', icon: Building },
      { title: 'Alavancagem', description: `Divida de curto prazo de ${formatM(balanceData.shortTermDebt)}M USD coberta ${cashCoverage}% pela caixa disponivel.`, severity: currentRatio >= 1.5 ? 'low' : 'medium', icon: CreditCard },
    ],
    en: [
      { title: 'Liquidity', description: `Current Ratio of ${currentRatio}x above 1.5x threshold. Comfortable current position, no immediate refinancing pressure.`, severity: 'low', icon: CheckCircle },
      { title: 'Working Capital', description: `Receivables with DSO of ${workingCapitalData.dso} days represents main source of tied-up capital. Monitor collection.`, severity: 'medium', icon: Clock },
      { title: 'Structure', description: `Fixed assets weight of ${fixedAssetsRatio}% in assets indicates capital intensity typical of mining operations.`, severity: 'low', icon: Building },
      { title: 'Leverage', description: `Short-term debt of ${formatM(balanceData.shortTermDebt)}M USD covered ${cashCoverage}% by available cash.`, severity: currentRatio >= 1.5 ? 'low' : 'medium', icon: CreditCard },
    ],
  };

  return (
    <div className="space-y-8" data-testid="balance-sheet-section">
      {/* Page Title */}
      <div>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Balanco Patrimonial' : 'Balance Sheet'} | {titleSuffix}
            </h1>
            <p className="text-[15px] leading-[22px]" style={{ color: '#425070' }}>
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
        {/* Finance Flow Map - appears below title, above key messages */}
        {flowMap && <div className="mt-4">{flowMap}</div>}
      </div>

      {/* ===================== */}
      {/* MENSAGENS-CHAVE - CAPEX Style */}
      {/* ===================== */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {balanceInsights[language].map((insight, idx) => {
            const IconComponent = insight.icon;
            return (
              <div 
                key={idx} 
                className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    idx === 0 ? 'bg-emerald-500/30' : idx === 1 ? 'bg-amber-500/30' : 'bg-blue-500/30'
                  }`}>
                    <IconComponent className={`w-5 h-5 ${
                      idx === 0 ? 'text-emerald-400' : idx === 1 ? 'text-amber-400' : 'text-blue-400'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-sm mb-1.5 leading-tight ${insight.color}`}>{insight.title}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{insight.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===================== */}
      {/* KPIs - Executive Synthesis Line */}
      {/* 5 angles: structural robustness, liquidity, working capital, WC efficiency, net financial pressure */}
      {/* ===================== */}
      <div className="grid grid-cols-5 gap-4 overflow-visible">
        {/* 1. Autonomia Financeira - Structural Robustness */}
        <MetricCardWithTooltip 
          title={language === 'pt' ? 'Autonomia Financeira' : 'Financial Autonomy'} 
          value={balanceData.financialAutonomy} 
          unit="%" 
          icon={Shield}
          color="gold"
          vsBudget={parseFloat(autonomyVsBudget)}
          vsYoY={parseFloat(autonomyYoY)}
          changeUnit="p.p."
          tooltipData={{
            title: language === 'pt' ? 'Autonomia Financeira' : 'Financial Autonomy',
            definition: language === 'pt' 
              ? 'Percentagem do activo total financiada por capitais próprios.'
              : 'Percentage of total assets financed by equity.',
            relevance: language === 'pt'
              ? 'Mede a robustez estrutural do balanço e o grau de independência financeira da empresa.'
              : 'Measures balance sheet structural strength and company financial independence.',
            currentReading: language === 'pt'
              ? `${balanceData.financialAutonomy}% indica que mais de metade do activo é suportado por capital próprio, reforçando a resiliência financeira e reduzindo dependência de financiamento externo.`
              : `${balanceData.financialAutonomy}% indicates more than half of assets are supported by equity, reinforcing financial resilience and reducing external financing dependence.`
          }}
        />
        
        {/* 2. Fundo de Maneio - Working Capital */}
        <MetricCardWithTooltip 
          title={language === 'pt' ? 'Fundo de Maneio' : 'Working Capital'} 
          value={formatM(workingCapitalData.netWorkingCapital)} 
          unit="M USD" 
          icon={ArrowLeftRight}
          color="emerald"
          vsBudget={parseFloat(wcVsBudget)}
          vsYoY={parseFloat(wcYoY)}
          tooltipData={{
            title: language === 'pt' ? 'Fundo de Maneio' : 'Working Capital',
            definition: language === 'pt'
              ? 'Diferença entre activo corrente e passivo corrente.'
              : 'Difference between current assets and current liabilities.',
            relevance: language === 'pt'
              ? 'Mostra a folga financeira disponível para suportar a operação corrente.'
              : 'Shows financial slack available to support current operations.',
            currentReading: language === 'pt'
              ? `${formatM(workingCapitalData.netWorkingCapital)}M USD representa uma posição corrente positiva${parseFloat(wcYoY) < 0 ? ', mas a redução face ao período comparativo indica maior pressão sobre o working capital' : ', reforçando a capacidade operacional'}.`
              : `${formatM(workingCapitalData.netWorkingCapital)}M USD represents a positive current position${parseFloat(wcYoY) < 0 ? ', but the reduction vs prior period indicates higher working capital pressure' : ', reinforcing operational capacity'}.`
          }}
        />
        
        {/* 3. Current Ratio - Liquidity */}
        <MetricCardWithTooltip 
          title="Current Ratio" 
          value={currentRatio} 
          unit="x" 
          icon={Activity}
          color="blue"
          vsBudget={parseFloat((parseFloat(currentRatio) - budgetCurrentRatio).toFixed(2))}
          vsYoY={parseFloat((parseFloat(currentRatio) - parseFloat(prevCurrentRatio)).toFixed(2))}
          changeUnit=""
          tooltipData={{
            title: 'Current Ratio',
            definition: language === 'pt'
              ? 'Rácio entre activo corrente e passivo corrente.'
              : 'Ratio between current assets and current liabilities.',
            relevance: language === 'pt'
              ? 'Mede a capacidade da empresa para cumprir obrigações de curto prazo com os activos correntes disponíveis.'
              : 'Measures company ability to meet short-term obligations with available current assets.',
            currentReading: language === 'pt'
              ? `${currentRatio}x ${parseFloat(currentRatio) >= 1.5 ? 'indica cobertura confortável do passivo corrente, com folga de liquidez adequada' : parseFloat(currentRatio) >= 1.0 ? 'indica cobertura do passivo corrente, mas com folga limitada' : 'abaixo de 1.0x requer atenção imediata à posição de liquidez'}.`
              : `${currentRatio}x ${parseFloat(currentRatio) >= 1.5 ? 'indicates comfortable current liability coverage with adequate liquidity slack' : parseFloat(currentRatio) >= 1.0 ? 'indicates current liability coverage but with limited slack' : 'below 1.0x requires immediate attention to liquidity position'}.`
          }}
        />
        
        {/* 4. Ciclo de Conversão de Caixa - WC Efficiency */}
        <MetricCardWithTooltip 
          title={language === 'pt' ? 'Ciclo Conv. Caixa' : 'Cash Conversion Cycle'} 
          value={workingCapitalData.ccc} 
          unit={language === 'pt' ? 'dias' : 'days'} 
          icon={RefreshCw}
          vsBudget={parseFloat((workingCapitalData.ccc - 95).toFixed(0))}
          vsYoY={parseFloat((workingCapitalData.ccc - prevWorkingCapitalData.ccc).toFixed(0))}
          changeUnit=""
          inverse={true}
          tooltipData={{
            title: language === 'pt' ? 'Ciclo de Conversão de Caixa' : 'Cash Conversion Cycle',
            definition: language === 'pt'
              ? 'Número de dias que a empresa demora a converter investimento operacional em caixa.'
              : 'Number of days company takes to convert operational investment into cash.',
            relevance: language === 'pt'
              ? 'Mede eficiência de working capital e capital empatado na operação.'
              : 'Measures working capital efficiency and capital tied up in operations.',
            currentReading: language === 'pt'
              ? `${workingCapitalData.ccc} dias significa que a empresa mantém capital relevante imobilizado no ciclo operacional${workingCapitalData.ccc < prevWorkingCapitalData.ccc ? '. A melhoria face a ' + (year - 1) + ' é positiva para a liquidez' : ''}.`
              : `${workingCapitalData.ccc} days means company keeps relevant capital tied up in operating cycle${workingCapitalData.ccc < prevWorkingCapitalData.ccc ? '. Improvement vs ' + (year - 1) + ' is positive for liquidity' : ''}.`
          }}
        />
        
        {/* 5. Dívida Líquida - Net Financial Pressure */}
        <MetricCardWithTooltip 
          title={language === 'pt' ? 'Divida Liquida' : 'Net Debt'} 
          value={formatM(netDebt)} 
          unit="M USD" 
          icon={Banknote}
          color="amber"
          vsBudget={parseFloat(((netDebt - budgetNetDebt) / Math.abs(budgetNetDebt) * 100).toFixed(1))}
          vsYoY={parseFloat(((netDebt - prevNetDebt) / Math.abs(prevNetDebt) * 100).toFixed(1))}
          inverse={true}
          subtitle={`${language === 'pt' ? 'Div.Liq./EBITDA' : 'ND/EBITDA'}: ${netDebtEbitda}x`}
          tooltipData={{
            title: language === 'pt' ? 'Dívida Líquida' : 'Net Debt',
            definition: language === 'pt'
              ? 'Dívida financeira bruta menos caixa e equivalentes.'
              : 'Gross financial debt minus cash and equivalents.',
            relevance: language === 'pt'
              ? 'Mostra a exposição financeira líquida real da empresa após considerar a liquidez disponível.'
              : 'Shows real net financial exposure after considering available liquidity.',
            currentReading: language === 'pt'
              ? `${formatM(netDebt)}M USD traduz o peso líquido do endividamento sobre a estrutura financeira. Com rácio Dív.Líq./EBITDA de ${netDebtEbitda}x, a alavancagem permanece ${parseFloat(netDebtEbitda) <= 2.0 ? 'em níveis confortáveis' : parseFloat(netDebtEbitda) <= 3.0 ? 'moderada' : 'elevada'}.`
              : `${formatM(netDebt)}M USD reflects net debt weight on financial structure. With Net Debt/EBITDA of ${netDebtEbitda}x, leverage remains ${parseFloat(netDebtEbitda) <= 2.0 ? 'at comfortable levels' : parseFloat(netDebtEbitda) <= 3.0 ? 'moderate' : 'elevated'}.`
          }}
        />
      </div>

      {/* ===================== */}
      {/* RESUMO EXECUTIVO */}
      {/* ===================== */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Balanço patrimonial e estrutura financeira' : 'Balance sheet and financial structure'}
        language={language}
        maxHeight="550px"
        icon={Info}
        dataTestId="balance-sheet-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Em ${year}, o balanço fechou com activo total de USD ${formatM(balanceData.totalAssets)}M, acima dos USD ${formatM(budgetTotalAssets)}M do orçamento e dos USD ${formatM(prevBalanceData.totalAssets)}M de ${year - 1}. A principal leitura estrutural é o reforço da capitalização: a autonomia financeira subiu para ${balanceData.financialAutonomy}%, o que significa que mais de metade do activo passou a estar financiado por capitais próprios. O balanço terminou mais forte em capitalização, mais eficiente em conversão de caixa e mais leve em alavancagem líquida.`
              : `In ${year}, the balance sheet closed with total assets of USD ${formatM(balanceData.totalAssets)}M, above the USD ${formatM(budgetTotalAssets)}M budget and USD ${formatM(prevBalanceData.totalAssets)}M in ${year - 1}. The main structural reading is the strengthening of capitalization: financial autonomy rose to ${balanceData.financialAutonomy}%, meaning more than half of assets are now financed by equity. The balance sheet ended stronger in capitalization, more efficient in cash conversion and lighter in net leverage.`}
          </p>
        </div>

        {/* Driver Sections - Collapsible Cards */}
        <div className="space-y-4">
          {/* Estrutura Financeira e Solidez */}
          <div 
            id="estrutura-financeira-solidez"
            className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Estrutura Financeira e Solidez' : 'Financial Structure and Strength'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Capitalização e perfil de financiamento' : 'Capitalization and financing profile'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `A autonomia financeira subiu para ${balanceData.financialAutonomy}%, acima de ${budgetAutonomyFinanceira}% no orçamento e de ${prevAutonomyFinanceira}% em ${year - 1}. Esta evolução significa que mais de metade do activo passou a estar financiado por capitais próprios, reduzindo a dependência de dívida externa e reforçando a solidez patrimonial da empresa.`
                : `Financial autonomy rose to ${balanceData.financialAutonomy}%, above ${budgetAutonomyFinanceira}% in budget and ${prevAutonomyFinanceira}% in ${year - 1}. This evolution means more than half of assets are now financed by equity, reducing dependence on external debt and strengthening the company's equity position.`}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O peso do imobilizado aumentou para ${fixedAssetsRatio}% do activo, acima de ${budgetFixedAssetsRatio}% no plano e de ${prevFixedAssetsRatio}% no período homólogo, confirmando maior intensidade de capital na base de activos. Do lado do passivo, o passivo corrente representou ${currentLiabRatio}% do total, abaixo dos ${budgetCurrentLiabRatio}% orçamentados, mas acima dos ${prevCurrentLiabRatio}% de ${year - 1}. Para a gerência, isto traduz um balanço simultaneamente mais robusto em capital próprio e mais exigente em gestão do passivo de curto prazo.`
                : `Fixed assets weight increased to ${fixedAssetsRatio}% of assets, above ${budgetFixedAssetsRatio}% in plan and ${prevFixedAssetsRatio}% in prior period, confirming greater capital intensity. On the liabilities side, current liabilities represented ${currentLiabRatio}% of total, below the ${budgetCurrentLiabRatio}% budgeted but above ${prevCurrentLiabRatio}% in ${year - 1}. For management, this translates to a balance sheet that is both stronger in equity and more demanding in short-term liability management.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Análise vs Orçamento e YoY' : 'Analysis vs Budget and YoY'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `Autonomia Financeira: ${balanceData.financialAutonomy}% (${(balanceData.financialAutonomy - budgetAutonomyFinanceira) >= 0 ? '+' : ''}${(balanceData.financialAutonomy - budgetAutonomyFinanceira).toFixed(1)}p.p. vs Orç., ${(balanceData.financialAutonomy - prevAutonomyFinanceira) >= 0 ? '+' : ''}${(balanceData.financialAutonomy - prevAutonomyFinanceira).toFixed(1)}p.p. YoY)` : `Financial Autonomy: ${balanceData.financialAutonomy}% (${(balanceData.financialAutonomy - budgetAutonomyFinanceira) >= 0 ? '+' : ''}${(balanceData.financialAutonomy - budgetAutonomyFinanceira).toFixed(1)}p.p. vs Budget, ${(balanceData.financialAutonomy - prevAutonomyFinanceira) >= 0 ? '+' : ''}${(balanceData.financialAutonomy - prevAutonomyFinanceira).toFixed(1)}p.p. YoY)`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `Activo Total: USD ${formatM(balanceData.totalAssets)}M (${((balanceData.totalAssets - budgetTotalAssets) / budgetTotalAssets * 100).toFixed(1)}% Orç.)` : `Total Assets: USD ${formatM(balanceData.totalAssets)}M (${((balanceData.totalAssets - budgetTotalAssets) / budgetTotalAssets * 100).toFixed(1)}% Budget)`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `Peso Imobilizado: ${fixedAssetsRatio}% do activo (${(fixedAssetsRatio - budgetFixedAssetsRatio) >= 0 ? '+' : ''}${(fixedAssetsRatio - budgetFixedAssetsRatio).toFixed(1)}p.p. vs Orç.)` : `Fixed Assets Ratio: ${fixedAssetsRatio}% of assets (${(fixedAssetsRatio - budgetFixedAssetsRatio) >= 0 ? '+' : ''}${(fixedAssetsRatio - budgetFixedAssetsRatio).toFixed(1)}p.p. vs Budget)`}
                </li>
              </ul>
            </div>
          </div>

          {/* Liquidez e Fundo de Maneio */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Droplets className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Liquidez e Fundo de Maneio' : 'Liquidity and Working Capital'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Posição corrente e capital circulante' : 'Current position and circulating capital'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `A posição corrente terminou o ano em níveis confortáveis. O Current Ratio fechou em ${currentRatio}x, acima do ${budgetCurrentRatio}x do orçamento e do threshold interno de 1,5x, embora abaixo de ${prevCurrentRatio}x em ${year - 1}. O Quick Ratio ficou em ${quickRatio}x, também acima do plano (${budgetQuickRatio}x), mas abaixo de ${prevQuickRatio}x no ano anterior. Em termos de leitura financeira, a liquidez corrente manteve-se sólida, mas com menor almofada relativa do que em ${year - 1}.`
                : `The current position ended the year at comfortable levels. Current Ratio closed at ${currentRatio}x, above the ${budgetCurrentRatio}x budget and internal threshold of 1.5x, although below ${prevCurrentRatio}x in ${year - 1}. Quick Ratio was ${quickRatio}x, also above plan (${budgetQuickRatio}x) but below ${prevQuickRatio}x in the prior year. In financial terms, current liquidity remained solid but with less cushion than in ${year - 1}.`}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O Fundo de Maneio reduziu-se de USD ${formatM(fmStart)}M para USD ${formatM(fmEnd)}M, ficando ${Math.abs(parseFloat(wcVsBudget)).toFixed(1)}% abaixo do orçamento e ${Math.abs(parseFloat(wcYoY)).toFixed(0)}% abaixo do período homólogo. A variação foi explicada por três movimentos principais: melhoria na cobrança de clientes que libertou capital, aumento de existências que absorveu parte do benefício, e redução do financiamento via fornecedores que diminuiu o suporte espontâneo ao working capital.`
                : `Working Capital decreased from USD ${formatM(fmStart)}M to USD ${formatM(fmEnd)}M, ${Math.abs(parseFloat(wcVsBudget)).toFixed(1)}% below budget and ${Math.abs(parseFloat(wcYoY)).toFixed(0)}% below prior period. The variation was explained by three main movements: improved collection that freed capital, increased inventory that absorbed part of the benefit, and reduced supplier financing that decreased spontaneous working capital support.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Variação do Fundo de Maneio' : 'Working Capital Variation'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Clientes (cobrança): ' : 'Receivables (collection): '}<span className={clientesImpact >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>{clientesImpact >= 0 ? '+' : ''}{formatM(clientesImpact)}M USD</span>{language === 'pt' ? ' — libertação de capital' : ' — capital release'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Existências (inventário): ' : 'Inventory: '}<span className={existenciasImpact >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>{existenciasImpact >= 0 ? '+' : ''}{formatM(existenciasImpact)}M USD</span>{language === 'pt' ? ' — absorção de capital' : ' — capital absorption'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Fornecedores (pagamentos): ' : 'Payables: '}<span className={fornecedoresImpact >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>{fornecedoresImpact >= 0 ? '+' : ''}{formatM(fornecedoresImpact)}M USD</span>{language === 'pt' ? ' — menor financiamento' : ' — reduced financing'}
                </li>
              </ul>
            </div>
          </div>

          {/* Ciclo de Conversão de Caixa */}
          <div className="bg-gradient-to-r from-violet-50 to-slate-50 rounded-xl p-5 border border-violet-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Ciclo de Conversão de Caixa' : 'Cash Conversion Cycle'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Eficiência operacional em dias' : 'Operational efficiency in days'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O ciclo de conversão de caixa melhorou materialmente para ${workingCapitalData.ccc} dias, uma redução de ${prevWorkingCapitalData.ccc - workingCapitalData.ccc} dias face a ${year - 1}. O principal driver foi o DSO, que caiu para ${workingCapitalData.dso} dias, indicando melhoria relevante na cobrança.`
                : `The cash conversion cycle improved materially to ${workingCapitalData.ccc} days, a reduction of ${prevWorkingCapitalData.ccc - workingCapitalData.ccc} days vs ${year - 1}. The main driver was DSO, which fell to ${workingCapitalData.dso} days, indicating relevant improvement in collection.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-violet-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Métricas de Ciclo' : 'Cycle Metrics'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  DSO: {workingCapitalData.dso} {language === 'pt' ? 'dias' : 'days'} <span className="text-emerald-600 font-semibold">(-{prevWorkingCapitalData.dso - workingCapitalData.dso} YoY)</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  DIO: {workingCapitalData.dio} {language === 'pt' ? 'dias' : 'days'} <span className="text-emerald-600 font-semibold">(-{prevWorkingCapitalData.dio - workingCapitalData.dio} YoY)</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  DPO: {workingCapitalData.dpo} {language === 'pt' ? 'dias' : 'days'} <span className="text-red-600 font-semibold">(-{prevWorkingCapitalData.dpo - workingCapitalData.dpo} YoY)</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Alavancagem e Risco Financeiro */}
          <div className="bg-gradient-to-r from-rose-50 to-slate-50 rounded-xl p-5 border border-rose-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                <Scale className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Alavancagem e Risco Financeiro' : 'Leverage and Financial Risk'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Posição de dívida e capacidade de serviço' : 'Debt position and service capacity'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `A posição de alavancagem melhorou. A Dívida Líquida fechou em USD ${formatM(netDebt)}M, com o rácio Dívida Líquida / EBITDA em ${netDebtEbitda}x, coerente com uma estrutura de financiamento equilibrada e sem pressão excessiva sobre capacidade de serviço.`
                : `The leverage position improved. Net Debt closed at USD ${formatM(netDebt)}M, with Net Debt/EBITDA ratio at ${netDebtEbitda}x, consistent with a balanced financing structure without excessive pressure on service capacity.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-rose-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Indicadores de Alavancagem' : 'Leverage Indicators'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                  {language === 'pt' ? `Dívida Líquida: USD ${formatM(netDebt)}M` : `Net Debt: USD ${formatM(netDebt)}M`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                  {language === 'pt' ? `Dív. Líquida / EBITDA: ${netDebtEbitda}x` : `Net Debt / EBITDA: ${netDebtEbitda}x`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                  {language === 'pt' ? `Current Ratio: ${currentRatio}x` : `Current Ratio: ${currentRatio}x`}
                </li>
              </ul>
            </div>
          </div>

          {/* Provisões para Outros Riscos e Encargos */}
          <div className="rounded-xl p-5" style={{ backgroundColor: '#F0FAF5', borderWidth: '1px', borderStyle: 'solid', borderColor: '#B7EBC8' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#D4F0E0' }}>
                <Shield className="w-5 h-5" style={{ color: '#0FA15D' }} />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Provisões para Outros Riscos e Encargos' : 'Provisions for Other Risks and Charges'}</h4>
                <p className="text-xs" style={{ color: '#067647' }}>{language === 'pt' ? 'Componente estrutural e ajustamento extraordinário' : 'Structural component and extraordinary adjustment'}</p>
              </div>
            </div>
            
            <div className="space-y-3 text-xs text-slate-600 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, a conta de <strong>Provisões para Outros Riscos e Encargos</strong> fechou em <strong style={{ color: '#0E1633' }}>USD 13,1M</strong>, acima do orçamento (<strong>+47%</strong>) e acima de 2024 (<strong>+54%</strong>). A evolução da rubrica resulta de dois movimentos distintos dentro do balanço: por um lado, o crescimento da <strong style={{ color: '#067647' }}>provisão ambiental</strong>, que subiu para USD 11,1M; por outro, a entrada de uma <strong style={{ color: '#9B7A00' }}>provisão para existências</strong> de USD 2,0M, reconhecida em 2025 e sem precedente no exercício anterior.</>
                  : <>In 2025, the <strong>Provisions for Other Risks and Charges</strong> account closed at <strong style={{ color: '#0E1633' }}>USD 13.1M</strong>, above budget (<strong>+47%</strong>) and above 2024 (<strong>+54%</strong>). The evolution results from two distinct movements: on one hand, the growth of the <strong style={{ color: '#067647' }}>environmental provision</strong>, which rose to USD 11.1M; on the other, the entry of an <strong style={{ color: '#9B7A00' }}>inventory provision</strong> of USD 2.0M, recognized in 2025 with no precedent in the prior year.</>}
              </p>
              
              <p>
                {language === 'pt' 
                  ? <>A leitura estrutural do balanço deve continuar centrada na <strong style={{ color: '#067647' }}>provisão ambiental</strong>, que representa <strong>85% do saldo total</strong> da conta. Esta componente tem natureza estrutural e de longo prazo, estando associada à obrigação de reabilitação das áreas de exploração mineira. O saldo aumentou face a 2024, sobretudo em função do aumento do custo estimado de reabilitação e da actualização dos pressupostos financeiros da obrigação. Em termos de balanço, esta rubrica não deve ser lida como passivo extraordinário de curto prazo, mas como reflexo da <strong>acumulação da responsabilidade futura</strong> gerada pela operação.</>
                  : <>The structural reading of the balance sheet should remain focused on the <strong style={{ color: '#067647' }}>environmental provision</strong>, which represents <strong>85% of the total balance</strong>. This component is structural and long-term in nature, associated with the obligation to rehabilitate mining exploration areas. The balance increased vs 2024, mainly due to increased estimated rehabilitation costs and updated financial assumptions. In balance sheet terms, this item should not be read as short-term extraordinary liability, but as a reflection of the <strong>accumulation of future responsibility</strong> generated by operations.</>}
              </p>
              
              <p>
                {language === 'pt' 
                  ? <>Em paralelo, a <strong style={{ color: '#9B7A00' }}>provisão para existências</strong> introduz uma componente diferente dentro da conta. Os USD 2,0M reconhecidos em 2025 têm <strong>natureza extraordinária e não recorrente</strong>, associada ao ajustamento de inventários de baixa rotação. Ao contrário da componente ambiental, esta provisão não representa obrigação estrutural de longo prazo ligada ao activo mineiro; representa antes uma correcção pontual da base de activos circulantes. Para a Gerência, esta distinção é importante porque impede que o aumento do saldo total da conta seja interpretado como agravamento homogéneo do risco estrutural do balanço.</>
                  : <>In parallel, the <strong style={{ color: '#9B7A00' }}>inventory provision</strong> introduces a different component within the account. The USD 2.0M recognized in 2025 is <strong>extraordinary and non-recurring</strong> in nature, associated with slow-moving inventory adjustments. Unlike the environmental component, this provision does not represent a long-term structural obligation linked to mining assets; rather, it represents a one-time correction of current assets base. For Management, this distinction is important as it prevents the total account balance increase from being interpreted as a homogeneous worsening of the balance sheet's structural risk.</>}
              </p>
              
              <p className="font-medium text-slate-700">
                {language === 'pt' ? 'Em termos executivos, esta conta deve ser lida em duas camadas:' : 'In executive terms, this account should be read in two layers:'}
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg p-3" style={{ backgroundColor: '#EEF9F3', borderWidth: '1px', borderStyle: 'solid', borderColor: '#B7EBC8' }}>
                  <p className="font-semibold mb-1" style={{ color: '#067647' }}>{language === 'pt' ? 'Componente Estrutural' : 'Structural Component'}</p>
                  <p className="text-slate-600">
                    {language === 'pt' 
                      ? <>Provisão ambiental de <strong>USD 11,1M</strong>, associada à obrigação futura de reabilitação</>
                      : <>Environmental provision of <strong>USD 11.1M</strong>, associated with future rehabilitation obligation</>}
                  </p>
                </div>
                <div className="rounded-lg p-3" style={{ backgroundColor: '#FCF7E8', borderWidth: '1px', borderStyle: 'solid', borderColor: '#EAD58A' }}>
                  <p className="font-semibold mb-1" style={{ color: '#9B7A00' }}>{language === 'pt' ? 'Componente Extraordinária' : 'Extraordinary Component'}</p>
                  <p className="text-slate-600">
                    {language === 'pt' 
                      ? <>Provisão para existências de <strong>USD 2,0M</strong>, de natureza não recorrente</>
                      : <>Inventory provision of <strong>USD 2.0M</strong>, non-recurring in nature</>}
                  </p>
                </div>
              </div>
              
              <p>
                {language === 'pt' 
                  ? <>A principal implicação para o balanço é que o crescimento desta rubrica em 2025 não decorre apenas de maior passivo estrutural; decorre também da entrada de um ajustamento pontual que <strong>não deve ser extrapolado linearmente</strong> para os próximos períodos. O foco de gestão deve, por isso, concentrar-se em dois pontos: <strong>acompanhar a evolução da provisão ambiental</strong> como obrigação de longo prazo e <strong>garantir que a componente de existências permanece um evento isolado</strong> e não recorrente.</>
                  : <>The main implication for the balance sheet is that this item's growth in 2025 does not stem only from higher structural liabilities; it also results from a one-time adjustment that <strong>should not be linearly extrapolated</strong> to future periods. Management focus should therefore concentrate on two points: <strong>monitoring environmental provision evolution</strong> as a long-term obligation and <strong>ensuring the inventory component remains an isolated</strong>, non-recurring event.</>}
              </p>
            </div>
            
            {/* Pontos a Reter */}
            <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#F8F9FC', borderWidth: '1px', borderStyle: 'solid', borderColor: '#E3E8F1' }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#131A45' }}>{language === 'pt' ? 'Pontos a Reter' : 'Key Takeaways'}</p>
              <ul className="text-[11px] text-slate-600 space-y-1">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full mt-1.5" style={{ backgroundColor: '#131A45' }}></div>
                  <span>{language === 'pt' ? 'Saldo total da conta: USD 13,1M' : 'Total account balance: USD 13.1M'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full mt-1.5" style={{ backgroundColor: '#0FA15D' }}></div>
                  <span>{language === 'pt' ? 'Provisão ambiental: USD 11,1M | 85% do total' : 'Environmental provision: USD 11.1M | 85% of total'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full mt-1.5" style={{ backgroundColor: '#C8A600' }}></div>
                  <span>{language === 'pt' ? 'Provisão para existências: USD 2,0M | componente nova em 2025' : 'Inventory provision: USD 2.0M | new component in 2025'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full mt-1.5" style={{ backgroundColor: '#667085' }}></div>
                  <span>{language === 'pt' ? 'Leitura de balanço: crescimento explicado por combinação entre obrigação estrutural e ajustamento extraordinário' : 'Balance sheet reading: growth explained by combination of structural obligation and extraordinary adjustment'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full mt-1.5" style={{ backgroundColor: '#667085' }}></div>
                  <span>{language === 'pt' ? 'Implicação de gestão: separar claramente o que é passivo recorrente de longo prazo do que é correcção pontual de activos' : 'Management implication: clearly separate long-term recurring liability from one-time asset correction'}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Síntese à Gerência */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Síntese à Gerência' : 'Management Synthesis'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Leitura consolidada do período' : 'Consolidated reading of the period'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Em ${year}, o balanço terminou mais forte em capitalização (autonomia financeira de ${balanceData.financialAutonomy}%), mais eficiente em conversão de caixa (CCC de ${workingCapitalData.ccc} dias) e mais leve em alavancagem líquida (Dív. Líquida USD ${formatM(netDebt)}M), mas com menor folga em fundo de maneio (USD ${formatM(fmEnd)}M) do que no período homólogo.`
                : `In ${year}, the balance sheet ended stronger in capitalization (financial autonomy at ${balanceData.financialAutonomy}%), more efficient in cash conversion (CCC of ${workingCapitalData.ccc} days) and lighter in net leverage (Net Debt USD ${formatM(netDebt)}M), but with less working capital cushion (USD ${formatM(fmEnd)}M) than in the same period last year.`}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed">
              {language === 'pt' 
                ? 'Para a gerência, a principal leitura é que o balanço está mais sólido e eficiente, mas a manutenção desta trajectória dependerá da capacidade de preservar disciplina de cobrança, controlar inventário e evitar nova pressão sobre o financiamento de curto prazo.'
                : 'For management, the main reading is that the balance sheet is more solid and efficient, but maintaining this trajectory will depend on the ability to preserve collection discipline, control inventory and avoid new pressure on short-term financing.'}
            </p>
          </div>
        </div>

        {/* ============ DRILLDOWN NAVIGATION CARDS ============ */}
        {setActiveSection && (
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {language === 'pt' ? 'Análises Relacionadas' : 'Related Analysis'}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <ExecutiveDrilldownCard
                icon={Wallet}
                title={language === 'pt' ? 'Liquidez' : 'Liquidity'}
                subtitle={language === 'pt' ? 'Caixa e geração' : 'Cash and generation'}
                navigateTo="liquidity"
                setActiveSection={setActiveSection}
                color="emerald"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Analise a posição de caixa e a geração de fluxo livre.'
                    : 'Analyze cash position and free cash flow generation.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={CreditCard}
                title={language === 'pt' ? 'Dívida' : 'Debt'}
                subtitle={language === 'pt' ? 'Estrutura e perfil' : 'Structure and profile'}
                navigateTo="debt"
                setActiveSection={setActiveSection}
                color="rose"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Veja o detalhe da dívida bancária e perfil de amortização.'
                    : 'See bank debt detail and amortization profile.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={Target}
                title={language === 'pt' ? 'CAPEX' : 'CAPEX'}
                subtitle={language === 'pt' ? 'Activos imobilizados' : 'Fixed assets'}
                navigateTo="capex"
                setActiveSection={setActiveSection}
                color="amber"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Analise os investimentos que sustentam a base de activos.'
                    : 'Analyze investments sustaining the asset base.'}
                </p>
              </ExecutiveDrilldownCard>
            </div>
          </div>
        )}
      </ExecutiveSummarySection>

      {/* ===================== */}
      {/* 1. ESTRUTURA FINANCEIRA E SOLIDEZ DO BALANCO */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Estrutura Financeira e Solidez do Balanco' : 'Financial Structure and Balance Sheet Strength'}
          </h3>
          <p className="text-sm text-slate-500">
            {language === 'pt' 
              ? `Composicao do activo e fontes de financiamento — comparativo ${year} vs Orcamento vs ${year - 1}` 
              : `Asset composition and financing sources — ${year} vs Budget vs ${year - 1} comparison`}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Assets Structure */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">{language === 'pt' ? 'Composicao do Activo' : 'Asset Composition'}</h4>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={structureComparisonData} layout="vertical" margin={{ left: 0, right: 50 }}>
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}M`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={55} />
                <Tooltip formatter={(v) => `${v.toFixed(1)}M USD`} />
                <Bar dataKey="Activo Corrente" stackId="a" fill="#3B82F6" name={language === 'pt' ? 'Activo Corrente' : 'Current Assets'} />
                <Bar dataKey="Activo Nao Corrente" stackId="a" fill="#0A192F" name={language === 'pt' ? 'Activo Nao Corrente' : 'Non-Current Assets'} radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="total" position="right" formatter={(v) => `${v.toFixed(0)}M`} fontSize={10} fill="#475569" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Legend */}
            <div className="flex justify-center gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#3B82F6]"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Act. Corrente' : 'Current'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#0A192F]"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Act. Nao Corrente' : 'Non-Current'}</span>
              </div>
            </div>
          </div>
          
          {/* Financing Structure */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">{language === 'pt' ? 'Fontes de Financiamento' : 'Financing Sources'}</h4>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={financingComparisonData} layout="vertical" margin={{ left: 0, right: 50 }}>
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}M`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={55} />
                <Tooltip formatter={(v) => `${v.toFixed(1)}M USD`} />
                <Bar dataKey="Passivo Corrente" stackId="a" fill="#F59E0B" name={language === 'pt' ? 'Passivo Corrente' : 'Current Liabilities'} />
                <Bar dataKey="Passivo Nao Corrente" stackId="a" fill="#C5A059" name={language === 'pt' ? 'Passivo Nao Corrente' : 'Non-Current Liabilities'} />
                <Bar dataKey="Capital Proprio" stackId="a" fill="#059669" name={language === 'pt' ? 'Capital Proprio' : 'Equity'} radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="total" position="right" formatter={(v) => `${v.toFixed(0)}M`} fontSize={10} fill="#475569" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Legend */}
            <div className="flex justify-center gap-3 mt-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#F59E0B]"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Pass. Corr.' : 'Curr. Liab.'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#C5A059]"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Pass. N/Corr.' : 'Non-Curr.'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#059669]"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Cap. Proprio' : 'Equity'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Structure KPIs with comparisons */}
        <div className="grid grid-cols-4 gap-4">
          {/* Autonomia Financeira */}
          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-medium text-slate-500">{language === 'pt' ? 'Autonomia Financeira' : 'Financial Autonomy'}</span>
            </div>
            <p className="font-serif text-2xl font-semibold text-slate-800">
              {balanceData.financialAutonomy}<span className="text-sm text-slate-400 ml-1">%</span>
            </p>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">{language === 'pt' ? 'vs Orc.' : 'vs Bud.'}</span>
                <span className={`font-medium ${balanceData.financialAutonomy >= budgetAutonomyFinanceira ? 'text-emerald-600' : 'text-red-600'}`}>
                  {budgetAutonomyFinanceira}% ({balanceData.financialAutonomy >= budgetAutonomyFinanceira ? '+' : ''}{(balanceData.financialAutonomy - budgetAutonomyFinanceira).toFixed(1)}p.p.)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{year - 1}</span>
                <span className={`font-medium ${balanceData.financialAutonomy >= prevAutonomyFinanceira ? 'text-emerald-600' : 'text-red-600'}`}>
                  {prevAutonomyFinanceira}% ({balanceData.financialAutonomy >= prevAutonomyFinanceira ? '+' : ''}{(balanceData.financialAutonomy - prevAutonomyFinanceira).toFixed(1)}p.p.)
                </span>
              </div>
            </div>
          </div>
          
          {/* Peso Imobilizado */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <Building className="w-4 h-4 text-slate-600" />
              <span className="text-xs font-medium text-slate-500">{language === 'pt' ? 'Peso Imobilizado' : 'Fixed Assets Weight'}</span>
            </div>
            <p className="font-serif text-2xl font-semibold text-slate-800">
              {fixedAssetsRatio}<span className="text-sm text-slate-400 ml-1">%</span>
            </p>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">{language === 'pt' ? 'vs Orc.' : 'vs Bud.'}</span>
                <span className={`font-medium ${parseFloat(fixedAssetsRatio) <= budgetFixedAssetsRatio ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {budgetFixedAssetsRatio}% ({parseFloat(fixedAssetsRatio) <= budgetFixedAssetsRatio ? '' : '+'}{(parseFloat(fixedAssetsRatio) - budgetFixedAssetsRatio).toFixed(1)}p.p.)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{year - 1}</span>
                <span className={`font-medium ${parseFloat(fixedAssetsRatio) <= parseFloat(prevFixedAssetsRatio) ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {prevFixedAssetsRatio}% ({parseFloat(fixedAssetsRatio) <= parseFloat(prevFixedAssetsRatio) ? '' : '+'}{(parseFloat(fixedAssetsRatio) - parseFloat(prevFixedAssetsRatio)).toFixed(1)}p.p.)
                </span>
              </div>
            </div>
          </div>
          
          {/* Pass. Corrente / Total */}
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-medium text-slate-500">{language === 'pt' ? 'Pass. Corrente / Total' : 'Current Liab. / Total'}</span>
            </div>
            <p className="font-serif text-2xl font-semibold text-slate-800">
              {currentLiabRatio}<span className="text-sm text-slate-400 ml-1">%</span>
            </p>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">{language === 'pt' ? 'vs Orc.' : 'vs Bud.'}</span>
                <span className={`font-medium ${parseFloat(currentLiabRatio) <= budgetCurrentLiabRatio ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {budgetCurrentLiabRatio}% ({parseFloat(currentLiabRatio) <= budgetCurrentLiabRatio ? '' : '+'}{(parseFloat(currentLiabRatio) - budgetCurrentLiabRatio).toFixed(1)}p.p.)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{year - 1}</span>
                <span className={`font-medium ${parseFloat(currentLiabRatio) <= parseFloat(prevCurrentLiabRatio) ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {prevCurrentLiabRatio}% ({parseFloat(currentLiabRatio) <= parseFloat(prevCurrentLiabRatio) ? '' : '+'}{(parseFloat(currentLiabRatio) - parseFloat(prevCurrentLiabRatio)).toFixed(1)}p.p.)
                </span>
              </div>
            </div>
          </div>
          
          {/* Cap. Próprio / Activo */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <PieChart className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-slate-500">{language === 'pt' ? 'Cap. Proprio / Activo' : 'Equity / Assets'}</span>
            </div>
            <p className="font-serif text-2xl font-semibold text-slate-800">
              {balanceData.financialAutonomy}<span className="text-sm text-slate-400 ml-1">%</span>
            </p>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">{language === 'pt' ? 'vs Orc.' : 'vs Bud.'}</span>
                <span className={`font-medium ${balanceData.financialAutonomy >= budgetAutonomyFinanceira ? 'text-emerald-600' : 'text-red-600'}`}>
                  {budgetAutonomyFinanceira}% ({balanceData.financialAutonomy >= budgetAutonomyFinanceira ? '+' : ''}{(balanceData.financialAutonomy - budgetAutonomyFinanceira).toFixed(1)}p.p.)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{year - 1}</span>
                <span className={`font-medium ${balanceData.financialAutonomy >= prevAutonomyFinanceira ? 'text-emerald-600' : 'text-red-600'}`}>
                  {prevAutonomyFinanceira}% ({balanceData.financialAutonomy >= prevAutonomyFinanceira ? '+' : ''}{(balanceData.financialAutonomy - prevAutonomyFinanceira).toFixed(1)}p.p.)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== */}
      {/* 2. LIQUIDEZ E FUNDO DE MANEIO */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Liquidez e Fundo de Maneio' : 'Liquidity and Working Capital'}
          </h3>
          <p className="text-sm text-slate-500">
            {language === 'pt' 
              ? 'Posicao corrente e drivers da variacao do fundo de maneio' 
              : 'Current position and working capital variation drivers'}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Liquidity Ratios */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-700">{language === 'pt' ? 'Racios de Liquidez' : 'Liquidity Ratios'}</h4>
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-slate-500">Current Ratio</span>
                  <span className={`text-xs font-semibold ${parseFloat(currentRatio) >= 1.5 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {parseFloat(currentRatio) >= 1.5 ? 'Confortavel' : 'Monitorizar'}
                  </span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="font-serif text-3xl font-bold text-slate-800">{currentRatio}</span>
                  <span className="text-slate-400 text-sm mb-1">x</span>
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{language === 'pt' ? 'Orcamento' : 'Budget'} {year}:</span>
                    <span className={`font-medium ${parseFloat(currentRatio) >= budgetCurrentRatio ? 'text-emerald-600' : 'text-red-600'}`}>
                      {budgetCurrentRatio}x ({parseFloat(currentRatio) >= budgetCurrentRatio ? '+' : ''}{(parseFloat(currentRatio) - budgetCurrentRatio).toFixed(2)})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{year - 1}:</span>
                    <span className={`font-medium ${parseFloat(currentRatio) >= parseFloat(prevCurrentRatio) ? 'text-emerald-600' : 'text-red-600'}`}>
                      {prevCurrentRatio}x ({parseFloat(currentRatio) >= parseFloat(prevCurrentRatio) ? '+' : ''}{(parseFloat(currentRatio) - parseFloat(prevCurrentRatio)).toFixed(2)})
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-slate-500">Quick Ratio</span>
                  <span className={`text-xs font-semibold ${parseFloat(quickRatio) >= 1.0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {parseFloat(quickRatio) >= 1.0 ? 'Confortavel' : 'Monitorizar'}
                  </span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="font-serif text-2xl font-bold text-slate-800">{quickRatio}</span>
                  <span className="text-slate-400 text-sm mb-1">x</span>
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{language === 'pt' ? 'Orcamento' : 'Budget'} {year}:</span>
                    <span className={`font-medium ${parseFloat(quickRatio) >= budgetQuickRatio ? 'text-emerald-600' : 'text-red-600'}`}>
                      {budgetQuickRatio}x ({parseFloat(quickRatio) >= budgetQuickRatio ? '+' : ''}{(parseFloat(quickRatio) - budgetQuickRatio).toFixed(2)})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{year - 1}:</span>
                    <span className={`font-medium ${parseFloat(quickRatio) >= parseFloat(prevQuickRatio) ? 'text-emerald-600' : 'text-red-600'}`}>
                      {prevQuickRatio}x ({parseFloat(quickRatio) >= parseFloat(prevQuickRatio) ? '+' : ''}{(parseFloat(quickRatio) - parseFloat(prevQuickRatio)).toFixed(2)})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Working Capital Waterfall - McKinsey Style (matching Cash Movement chart) */}
          <div className="col-span-2">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">
              {language === 'pt' ? 'Cascata do Fundo de Maneio' : 'Working Capital Waterfall'}
            </h4>
            <p className="text-xs text-slate-500 mb-3">
              {language === 'pt' 
                ? `Como o FM passou de ${formatM(fmStart)}M em ${year - 1} para ${formatM(fmEnd)}M em ${year}?`
                : `How did WC move from ${formatM(fmStart)}M in ${year - 1} to ${formatM(fmEnd)}M in ${year}?`
              }
            </p>
            
            {/* Classic Waterfall Chart - McKinsey Style */}
            <div className="relative" style={{ height: '260px' }}>
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 h-48 w-10 flex flex-col justify-between text-right pr-2">
                <span className="text-xs text-slate-400">100M</span>
                <span className="text-xs text-slate-400">75M</span>
                <span className="text-xs text-slate-400">50M</span>
                <span className="text-xs text-slate-400">25M</span>
                <span className="text-xs text-slate-400">0M</span>
              </div>
              
              {/* Grid lines */}
              <div className="absolute left-12 right-4 top-0 h-48 flex flex-col justify-between pointer-events-none">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="border-t border-dashed border-slate-200 w-full"></div>
                ))}
              </div>
              
              {/* Chart area */}
              <div className="absolute left-14 right-4 top-0 h-48 flex items-end justify-between gap-4">
                {(() => {
                  const maxValue = 100;
                  const chartHeight = 192; // 48 * 4
                  const scale = chartHeight / maxValue;
                  
                  const bars = [
                    { 
                      label: `FM ${year - 1}`, 
                      value: fmStart, 
                      height: fmStart * scale, 
                      bottom: 0, 
                      type: 'anchor' 
                    },
                    { 
                      label: language === 'pt' ? 'Clientes' : 'Receivables', 
                      value: clientesImpact, 
                      height: Math.abs(clientesImpact) * scale, 
                      bottom: clientesImpact >= 0 ? cumul1 * scale : cumul2 * scale, 
                      type: clientesImpact >= 0 ? 'positive' : 'negative' 
                    },
                    { 
                      label: language === 'pt' ? 'Existencias' : 'Inventory', 
                      value: existenciasImpact, 
                      height: Math.abs(existenciasImpact) * scale, 
                      bottom: existenciasImpact >= 0 ? cumul2 * scale : cumul3 * scale, 
                      type: existenciasImpact >= 0 ? 'positive' : 'negative' 
                    },
                    { 
                      label: language === 'pt' ? 'Fornecedores' : 'Suppliers', 
                      value: fornecedoresImpact, 
                      height: Math.abs(fornecedoresImpact) * scale, 
                      bottom: fornecedoresImpact >= 0 ? cumul3 * scale : cumul4 * scale, 
                      type: fornecedoresImpact >= 0 ? 'positive' : 'negative' 
                    },
                    { 
                      label: `FM ${year}`, 
                      value: fmEnd, 
                      height: fmEnd * scale, 
                      bottom: 0, 
                      type: 'anchor' 
                    }
                  ];
                  
                  return bars.map((bar, idx) => (
                    <div key={idx} className="flex-1 relative h-full">
                      {/* Value label above bar */}
                      <div 
                        className="absolute left-1/2 -translate-x-1/2 text-center"
                        style={{ bottom: `${bar.bottom + bar.height + 4}px` }}
                      >
                        <span className={`text-xs font-semibold ${
                          bar.type === 'anchor' ? 'text-slate-700' : 
                          bar.type === 'positive' ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {bar.type === 'anchor' ? '' : (bar.value >= 0 ? '+' : '')}{formatM(bar.value)}M
                        </span>
                      </div>
                      {/* Bar */}
                      <div 
                        className={`absolute left-1/2 -translate-x-1/2 w-12 rounded-t ${
                          bar.type === 'anchor' ? 'bg-[#0A192F]' : 
                          bar.type === 'positive' ? 'bg-emerald-500' : 'bg-red-500'
                        }`}
                        style={{ bottom: `${bar.bottom}px`, height: `${bar.height}px` }}
                      />
                    </div>
                  ));
                })()}
              </div>
              
              {/* Baseline */}
              <div className="absolute left-12 right-4 top-48 border-t-2 border-slate-300"></div>
              
              {/* X-axis labels */}
              <div className="absolute left-14 right-4 top-[200px] flex justify-between gap-4">
                {[
                  `FM ${year - 1}`,
                  language === 'pt' ? 'Clientes' : 'Receivables',
                  language === 'pt' ? 'Existencias' : 'Inventory',
                  language === 'pt' ? 'Fornecedores' : 'Suppliers',
                  `FM ${year}`
                ].map((label, idx) => (
                  <div key={idx} className="flex-1 text-center">
                    <span className="text-[10px] text-slate-500 leading-tight block">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Legend */}
            <div className="flex justify-center gap-6 mt-2 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#0A192F]"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Saldo FM' : 'WC Balance'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-emerald-500"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Aumenta FM' : 'Increases WC'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Reduz FM' : 'Decreases WC'}</span>
              </div>
            </div>
            
            {/* Executive Summary Text */}
            <div className="bg-slate-50 border-l-4 border-gold-500 rounded-r-lg px-4 py-2.5 mt-3">
              <p className="text-xs text-slate-700 leading-relaxed">
                {language === 'pt' ? wcSummaryPt : wcSummaryEn}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== */}
      {/* 3. CAPITAL EMPATADO E CICLO DE CONVERSAO DE CAIXA */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Capital Empatado e Ciclo de Conversao de Caixa' : 'Tied-up Capital and Cash Conversion Cycle'}
          </h3>
          <p className="text-sm text-slate-500">
            {language === 'pt' 
              ? 'Eficiencia do working capital e principais drivers' 
              : 'Working capital efficiency and main drivers'}
          </p>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <MiniKPICard 
            title="DSO"
            value={workingCapitalData.dso}
            unit={language === 'pt' ? 'dias' : 'days'}
            change={workingCapitalData.dso - prevWorkingCapitalData.dso}
            changeLabel={language === 'pt' ? 'dias YoY' : 'days YoY'}
            isPositive={workingCapitalData.dso <= prevWorkingCapitalData.dso}
            icon={Clock}
            color="blue"
          />
          <MiniKPICard 
            title="DIO"
            value={workingCapitalData.dio}
            unit={language === 'pt' ? 'dias' : 'days'}
            change={workingCapitalData.dio - prevWorkingCapitalData.dio}
            changeLabel={language === 'pt' ? 'dias YoY' : 'days YoY'}
            isPositive={workingCapitalData.dio <= prevWorkingCapitalData.dio}
            icon={Layers}
            color="gold"
          />
          <MiniKPICard 
            title="DPO"
            value={workingCapitalData.dpo}
            unit={language === 'pt' ? 'dias' : 'days'}
            change={workingCapitalData.dpo - prevWorkingCapitalData.dpo}
            changeLabel={language === 'pt' ? 'dias YoY' : 'days YoY'}
            isPositive={workingCapitalData.dpo >= prevWorkingCapitalData.dpo}
            icon={CreditCard}
            color="emerald"
          />
          <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-lg p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="w-4 h-4 text-gold-400" />
              <span className="text-xs font-medium text-gold-400">CCC</span>
            </div>
            <p className="font-serif text-3xl font-bold">
              {workingCapitalData.ccc}<span className="text-lg text-slate-400 ml-1">{language === 'pt' ? 'dias' : 'days'}</span>
            </p>
            <p className="text-xs text-emerald-400 mt-1">
              -{prevWorkingCapitalData.ccc - workingCapitalData.ccc} {language === 'pt' ? 'dias vs' : 'days vs'} {year - 1}
            </p>
          </div>
        </div>

        {/* CCC Decomposition */}
        <div className="bg-slate-50 border-l-4 border-gold-500 rounded-r-lg p-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            {language === 'pt' 
              ? `A melhoria do ciclo de conversao de caixa foi suportada por melhor cobranca (DSO ${workingCapitalData.dso < prevWorkingCapitalData.dso ? 'reduzido' : 'estavel'}) e rotacao de inventario (DIO ${workingCapitalData.dio < prevWorkingCapitalData.dio ? 'optimizado' : 'estavel'}), ${workingCapitalData.dpo >= prevWorkingCapitalData.dpo ? 'com manutencao do' : 'parcialmente compensada por menor'} prazo medio de pagamento a fornecedores.`
              : `Cash conversion cycle improvement was supported by better collection (DSO ${workingCapitalData.dso < prevWorkingCapitalData.dso ? 'reduced' : 'stable'}) and inventory turnover (DIO ${workingCapitalData.dio < prevWorkingCapitalData.dio ? 'optimized' : 'stable'}), ${workingCapitalData.dpo >= prevWorkingCapitalData.dpo ? 'with maintained' : 'partially offset by lower'} average supplier payment terms.`
            }
          </p>
        </div>
      </div>

      {/* ===================== */}
      {/* 4. DIVIDA, ALAVANCAGEM E CAPACIDADE DE FINANCIAMENTO */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Divida, Alavancagem e Capacidade de Financiamento' : 'Debt, Leverage and Financing Capacity'}
          </h3>
          <p className="text-sm text-slate-500">
            {language === 'pt' 
              ? 'Estrutura da divida, maturidade e sustentabilidade' 
              : 'Debt structure, maturity and sustainability'}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Debt Structure Chart */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">
              {language === 'pt' ? 'Estrutura da Divida' : 'Debt Structure'}
            </h4>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={debtStructureData} margin={{ left: 0, right: 40, top: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}M`} />
                <Tooltip formatter={(v) => `${v.toFixed(1)}M USD`} />
                <Bar dataKey="Curto Prazo" stackId="a" fill="#F59E0B" name={language === 'pt' ? 'Curto Prazo' : 'Short-Term'} />
                <Bar dataKey="Longo Prazo" stackId="a" fill="#0A192F" name={language === 'pt' ? 'Longo Prazo' : 'Long-Term'} radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="total" position="top" formatter={(v) => `${v.toFixed(0)}M`} fontSize={10} fill="#475569" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Legend */}
            <div className="flex justify-center gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#F59E0B]"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Curto Prazo' : 'Short-Term'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#0A192F]"></div>
                <span className="text-xs text-slate-600">{language === 'pt' ? 'Longo Prazo' : 'Long-Term'}</span>
              </div>
            </div>
          </div>

          {/* Debt Metrics with comparisons */}
          <div className="col-span-2 grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Banknote className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-medium text-slate-500">{language === 'pt' ? 'Divida Bruta' : 'Gross Debt'}</span>
                </div>
                <p className="font-serif text-2xl font-semibold text-slate-800">
                  {formatM(grossDebt)}<span className="text-sm text-slate-400 ml-1">M USD</span>
                </p>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{language === 'pt' ? 'vs Orc.' : 'vs Bud.'}</span>
                    <span className={`font-medium ${grossDebt <= budgetGrossDebt ? 'text-emerald-600' : 'text-red-600'}`}>
                      {grossDebt <= budgetGrossDebt ? '' : '+'}{((grossDebt - budgetGrossDebt) / budgetGrossDebt * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">YoY</span>
                    <span className={`font-medium ${grossDebt <= prevGrossDebt ? 'text-emerald-600' : 'text-red-600'}`}>
                      {((grossDebt - prevGrossDebt) / prevGrossDebt * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Scale className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-medium text-slate-500">{language === 'pt' ? 'Divida Liquida' : 'Net Debt'}</span>
                </div>
                <p className="font-serif text-2xl font-semibold text-slate-800">
                  {formatM(netDebt)}<span className="text-sm text-slate-400 ml-1">M USD</span>
                </p>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{language === 'pt' ? 'vs Orc.' : 'vs Bud.'}</span>
                    <span className={`font-medium ${netDebt <= budgetNetDebt ? 'text-emerald-600' : 'text-red-600'}`}>
                      {netDebt <= budgetNetDebt ? '' : '+'}{((netDebt - budgetNetDebt) / Math.abs(budgetNetDebt) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">YoY</span>
                    <span className={`font-medium ${netDebt <= prevNetDebt ? 'text-emerald-600' : 'text-red-600'}`}>
                      {((netDebt - prevNetDebt) / Math.abs(prevNetDebt) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <div className="flex items-center gap-2 mb-2">
                  <Wallet className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-medium text-slate-500">{language === 'pt' ? 'Caixa Disponivel' : 'Available Cash'}</span>
                </div>
                <p className="font-serif text-2xl font-semibold text-slate-800">
                  {formatM(balanceData.cash)}<span className="text-sm text-slate-400 ml-1">M USD</span>
                </p>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{language === 'pt' ? 'vs Orc.' : 'vs Bud.'}</span>
                    <span className={`font-medium ${balanceData.cash >= budgetCash ? 'text-emerald-600' : 'text-red-600'}`}>
                      {balanceData.cash >= budgetCash ? '+' : ''}{((balanceData.cash - budgetCash) / budgetCash * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">YoY</span>
                    <span className={`font-medium ${balanceData.cash >= prevBalanceData.cash ? 'text-emerald-600' : 'text-red-600'}`}>
                      {((balanceData.cash - prevBalanceData.cash) / prevBalanceData.cash * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-medium text-slate-500">{language === 'pt' ? 'Div. Liq. / EBITDA' : 'Net Debt / EBITDA'}</span>
                </div>
                <p className="font-serif text-2xl font-semibold text-slate-800">
                  {netDebtEbitda}<span className="text-sm text-slate-400 ml-1">x</span>
                </p>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{language === 'pt' ? 'vs Orc.' : 'vs Bud.'}</span>
                    <span className={`font-medium ${parseFloat(netDebtEbitda) <= budgetNetDebtEbitda ? 'text-emerald-600' : 'text-red-600'}`}>
                      {budgetNetDebtEbitda}x ({parseFloat(netDebtEbitda) <= budgetNetDebtEbitda ? '' : '+'}{(parseFloat(netDebtEbitda) - budgetNetDebtEbitda).toFixed(1)})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{year - 1}</span>
                    <span className={`font-medium ${parseFloat(netDebtEbitda) <= 1.3 ? 'text-emerald-600' : 'text-red-600'}`}>
                      1.3x ({parseFloat(netDebtEbitda) <= 1.3 ? '' : '+'}{(parseFloat(netDebtEbitda) - 1.3).toFixed(1)})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Debt Coverage Insight */}
        <div className="mt-4 bg-slate-50 rounded-lg p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Cobertura da Divida CP pela Caixa' : 'ST Debt Coverage by Cash'}</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${parseFloat(cashCoverage) >= 100 ? 'bg-emerald-500' : parseFloat(cashCoverage) >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(parseFloat(cashCoverage), 100)}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-slate-700">{cashCoverage}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== */}
      {/* 5. PROVISÕES PARA OUTROS RISCOS E ENCARGOS */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {/* Nível 1 — Título da Secção */}
        <div className="mb-6">
          <h3 className="font-serif text-lg font-semibold mb-1" style={{ color: '#0E1633' }}>
            {language === 'pt' ? 'Provisões para Outros Riscos e Encargos' : 'Provisions for Other Risks and Charges'}
          </h3>
          <p className="text-sm text-slate-500">
            {language === 'pt' 
              ? 'Obrigações não correntes associadas a riscos ambientais, existências e outros encargos' 
              : 'Non-current obligations related to environmental risks, inventory and other charges'}
          </p>
        </div>

        {/* Nível 2 — KPIs Executivos */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {/* KPI 1: Saldo Total 2025 */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
            <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F3F5FA' }}>
              <Shield className="w-4 h-4" style={{ color: '#131A45' }} />
            </div>
            <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Saldo Total 2025' : 'Total Balance 2025'}</p>
            <p className="font-serif text-3xl font-semibold" style={{ color: '#0E1633' }}>
              13.1<span className="text-base text-slate-400 ml-1 font-sans">M USD</span>
            </p>
            <div className="flex flex-col gap-1 mt-2">
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-rose-100 text-rose-700">
                <TrendingUp className="w-2.5 h-2.5" />
                +47% Orç.
              </span>
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-rose-100 text-rose-700">
                <TrendingUp className="w-2.5 h-2.5" />
                +54% YoY
              </span>
            </div>
          </div>

          {/* KPI 2: Provisão Ambiental 2025 */}
          <div className="bg-white rounded-xl p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible" style={{ borderWidth: '1px', borderStyle: 'solid', borderColor: '#B7EBC8' }}>
            <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EAF8F1' }}>
              <Droplets className="w-4 h-4" style={{ color: '#0FA15D' }} />
            </div>
            <p className="text-xs tracking-wider mb-1 pr-8" style={{ color: '#067647' }}>{language === 'pt' ? 'Provisão Ambiental' : 'Environmental Provision'}</p>
            <p className="font-serif text-3xl font-semibold" style={{ color: '#0E1633' }}>
              11.1<span className="text-base text-slate-400 ml-1 font-sans">M USD</span>
            </p>
            <div className="flex flex-col gap-1 mt-2">
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-emerald-100 text-emerald-700">
                <TrendingDown className="w-2.5 h-2.5" />
                -5% Orç.
              </span>
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-rose-100 text-rose-700">
                <TrendingUp className="w-2.5 h-2.5" />
                +31% YoY
              </span>
            </div>
          </div>

          {/* KPI 3: Provisões para Existências 2025 */}
          <div className="bg-white rounded-xl p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible" style={{ borderWidth: '1px', borderStyle: 'solid', borderColor: '#EAD58A' }}>
            <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FBF5DA' }}>
              <Layers className="w-4 h-4" style={{ color: '#C8A600' }} />
            </div>
            <p className="text-xs tracking-wider mb-1 pr-8" style={{ color: '#9B7A00' }}>{language === 'pt' ? 'Provisões Existências' : 'Inventory Provisions'}</p>
            <p className="font-serif text-3xl font-semibold" style={{ color: '#0E1633' }}>
              2.0<span className="text-base text-slate-400 ml-1 font-sans">M USD</span>
            </p>
            <div className="flex flex-col gap-1 mt-2">
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit" style={{ backgroundColor: '#FBF5DA', color: '#9B7A00' }}>
                <TrendingUp className="w-2.5 h-2.5" />
                +∞ vs Orç.
              </span>
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit" style={{ backgroundColor: '#FBF5DA', color: '#9B7A00' }}>
                <span className="text-[9px]">{language === 'pt' ? 'NOVO' : 'NEW'}</span>
              </span>
            </div>
          </div>

          {/* KPI 4: Peso da Provisão Ambiental */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
            <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EAF8F1' }}>
              <PieChart className="w-4 h-4" style={{ color: '#0FA15D' }} />
            </div>
            <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Peso Prov. Ambiental' : 'Env. Prov. Weight'}</p>
            <p className="font-serif text-3xl font-semibold" style={{ color: '#0E1633' }}>
              85<span className="text-base text-slate-400 ml-1 font-sans">%</span>
            </p>
            <div className="flex flex-col gap-1 mt-2">
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-emerald-100 text-emerald-700">
                <TrendingDown className="w-2.5 h-2.5" />
                -15p.p. vs 2024
              </span>
            </div>
          </div>
        </div>

        {/* Nível 3 — Gráfico Principal 2024 vs 2025 */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-1" style={{ color: '#0E1633' }}>
            {language === 'pt' ? 'Composição da Conta Provisões para Outros Riscos e Encargos | 2024 vs 2025' : 'Provisions for Other Risks and Charges Composition | 2024 vs 2025'}
          </h4>
          <p className="text-xs text-slate-500 mb-4">
            {language === 'pt' ? 'Reconhecimento inicial acumulado em 2024 e evolução em 2025' : 'Initial accumulated recognition in 2024 and evolution in 2025'}
          </p>
          
          <div className="relative" style={{ height: '320px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { year: '2024', ambiental: 8.5, existencias: 0, outros: 0.0, total: 8.5 },
                  { year: '2025', ambiental: 11.1, existencias: 2.0, outros: 0.0, total: 13.1 },
                ]}
                margin={{ top: 35, right: 40, left: 40, bottom: 60 }}
                barCategoryGap="40%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E3E8F1" vertical={false} />
                <XAxis 
                  dataKey="year" 
                  tick={{ fontSize: 14, fill: '#131A45', fontWeight: 600 }} 
                  stroke="#94A3B8"
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#64748B' }} 
                  stroke="#94A3B8"
                  tickFormatter={(v) => `${v}M`}
                  domain={[0, 16]}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const isFirst = label === '2024';
                      return (
                        <div className="bg-[#0A192F] text-white rounded-lg px-4 py-3 shadow-xl border border-slate-700 max-w-xs">
                          <p className="font-semibold text-gold-400 mb-2">{label}</p>
                          {isFirst && (
                            <p className="text-[10px] text-slate-400 mb-2 italic">
                              {language === 'pt' 
                                ? 'Reconhecimento inicial acumulado (2021–2024)'
                                : 'Initial accumulated recognition (2021–2024)'}
                            </p>
                          )}
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between gap-4">
                              <span style={{ color: '#0FA15D' }}>{language === 'pt' ? 'Provisão Ambiental:' : 'Environmental Provision:'}</span>
                              <span className="font-semibold">{payload[0]?.value}M USD</span>
                            </div>
                            {payload[1]?.value > 0 && (
                              <div className="flex justify-between gap-4">
                                <span style={{ color: '#C8A600' }}>{language === 'pt' ? 'Prov. Existências:' : 'Inventory Prov.:'}</span>
                                <span className="font-semibold">{payload[1]?.value}M USD</span>
                              </div>
                            )}
                            <div className="flex justify-between gap-4 pt-1 border-t border-slate-600">
                              <span className="text-white font-medium">Total:</span>
                              <span className="font-bold">{payload[0]?.payload?.total}M USD</span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="ambiental" stackId="a" fill="#0FA15D" radius={[0, 0, 0, 0]} name={language === 'pt' ? 'Provisão Ambiental' : 'Environmental Provision'} />
                <Bar dataKey="existencias" stackId="a" fill="#C8A600" radius={[0, 0, 0, 0]} name={language === 'pt' ? 'Prov. Existências' : 'Inventory Prov.'} />
                <Bar dataKey="outros" stackId="a" fill="#B8C0D9" radius={[4, 4, 0, 0]} name={language === 'pt' ? 'Outros' : 'Others'}>
                  <LabelList 
                    dataKey="total" 
                    position="top" 
                    formatter={(v) => `${v}M`}
                    style={{ fontSize: 14, fontWeight: 700, fill: '#131A45' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            
            {/* Annotation for 2024 bar */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-center" style={{ paddingLeft: '40px', paddingRight: '40px' }}>
              <div className="flex gap-8" style={{ width: '100%', maxWidth: '400px' }}>
                {/* 2024 annotation */}
                <div className="flex-1 text-center">
                  <div className="inline-block px-2 py-1 rounded text-[9px] font-medium" style={{ backgroundColor: '#EAF8F1', color: '#067647', border: '1px dashed #0FA15D' }}>
                    {language === 'pt' ? 'Reconhecimento inicial acumulado' : 'Initial accumulated recognition'}
                    <br />
                    <span className="font-semibold">(2021–2024)</span>
                  </div>
                </div>
                {/* 2025 annotation */}
                <div className="flex-1 text-center">
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded text-[9px] font-medium" style={{ backgroundColor: '#FBF5DA', color: '#9B7A00', border: '1px dashed #C8A600' }}>
                    <span>{language === 'pt' ? '+Existências (novo)' : '+Inventory (new)'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Legenda */}
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#0FA15D' }}></div>
              <span className="text-xs text-slate-600">{language === 'pt' ? 'Provisão Ambiental' : 'Environmental Provision'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#C8A600' }}></div>
              <span className="text-xs text-slate-600">{language === 'pt' ? 'Provisões Existências' : 'Inventory Provisions'}</span>
            </div>
          </div>
        </div>

        {/* Nível 4 — Caixa "Leitura Executiva para o Board" */}
        <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: '#F8F9FC', borderWidth: '1px', borderStyle: 'solid', borderColor: '#E3E8F1' }}>
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: '#0E1633' }}>
            <Target className="w-4 h-4" style={{ color: '#131A45' }} />
            {language === 'pt' ? 'Leitura Executiva para o Board' : 'Executive Reading for the Board'}
          </h4>
          <div className="space-y-3 text-sm leading-relaxed" style={{ color: '#344054' }}>
            <p>
              {language === 'pt' 
                ? <>A componente ambiental da conta <strong>"Provisões para Outros Riscos e Encargos"</strong> foi reconhecida contabilisticamente em <strong>2024</strong>, mas correspondeu ao <strong>valor acumulado relativo aos períodos de 2021 a 2024</strong>. Este reconhecimento inicial totalizou <strong>USD 8,5M</strong>, refletindo a obrigação de reabilitação das áreas de exploração mineira.</>
                : <>The environmental component of the <strong>"Provisions for Other Risks and Charges"</strong> account was recognized in <strong>2024</strong>, but corresponded to the <strong>accumulated value for the periods from 2021 to 2024</strong>. This initial recognition totaled <strong>USD 8.5M</strong>, reflecting the obligation to rehabilitate mining exploration areas.</>}
            </p>
            <p>
              {language === 'pt' 
                ? <>Em <strong>2025</strong>, a evolução da conta reflecte sobretudo o <strong>crescimento da obrigação ambiental</strong> (de USD 8,5M para USD 11,1M, <strong>+31%</strong>) e a <strong>entrada da componente extraordinária de existências</strong> (USD 2,0M). A provisão ambiental continua a representar <strong>85% do total</strong>, enquanto a provisão para existências é de natureza <strong>não recorrente</strong>.</>
                : <>In <strong>2025</strong>, the account evolution mainly reflects the <strong>growth of the environmental obligation</strong> (from USD 8.5M to USD 11.1M, <strong>+31%</strong>) and the <strong>entry of the extraordinary inventory component</strong> (USD 2.0M). The environmental provision continues to represent <strong>85% of total</strong>, while the inventory provision is <strong>non-recurring</strong> in nature.</>}
            </p>
          </div>
        </div>

        {/* Nível 5 — Dois Cards Analíticos Finais */}
        <div className="grid grid-cols-2 gap-4">
          {/* Card 1: Provisão Ambiental */}
          <div className="rounded-xl p-5" style={{ backgroundColor: '#EEF9F3', borderWidth: '1px', borderStyle: 'solid', borderColor: '#B7EBC8' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#D4F0E0' }}>
                <Droplets className="w-5 h-5" style={{ color: '#0FA15D' }} />
              </div>
              <div>
                <h4 className="font-semibold text-sm" style={{ color: '#0E1633' }}>{language === 'pt' ? 'Provisão Ambiental' : 'Environmental Provision'}</h4>
                <p className="text-xs" style={{ color: '#067647' }}>{language === 'pt' ? 'Componente estrutural | Obrigação de longo prazo' : 'Structural component | Long-term obligation'}</p>
              </div>
            </div>
            <div className="space-y-2 text-xs leading-relaxed" style={{ color: '#344054' }}>
              <p>
                {language === 'pt' 
                  ? <>A provisão ambiental de <strong style={{ color: '#067647' }}>USD 11,1M</strong> cobre a obrigação de <strong>reabilitação das áreas de exploração mineira</strong> após encerramento. O saldo aumentou <strong>31% YoY</strong> devido ao <strong>aumento do custo estimado de reabilitação</strong> e à <strong>actualização da taxa de desconto</strong>.</>
                  : <>The environmental provision of <strong style={{ color: '#067647' }}>USD 11.1M</strong> covers the obligation to <strong>rehabilitate mining exploration areas</strong> after closure. The balance increased <strong>31% YoY</strong> due to <strong>increased estimated rehabilitation cost</strong> and <strong>discount rate update</strong>.</>}
              </p>
              <div className="flex items-center gap-3 mt-3 pt-3" style={{ borderTop: '1px solid #B7EBC8' }}>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#D4F0E0', color: '#067647' }}>
                  {language === 'pt' ? 'ESTRUTURAL' : 'STRUCTURAL'}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#D4F0E0', color: '#067647' }}>
                  85% {language === 'pt' ? 'DO TOTAL' : 'OF TOTAL'}
                </span>
              </div>
            </div>
          </div>

          {/* Card 2: Provisões para Existências */}
          <div className="rounded-xl p-5" style={{ backgroundColor: '#FCF7E8', borderWidth: '1px', borderStyle: 'solid', borderColor: '#EAD58A' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#F5EBC4' }}>
                <Layers className="w-5 h-5" style={{ color: '#C8A600' }} />
              </div>
              <div>
                <h4 className="font-semibold text-sm" style={{ color: '#0E1633' }}>{language === 'pt' ? 'Provisões para Existências' : 'Inventory Provisions'}</h4>
                <p className="text-xs" style={{ color: '#9B7A00' }}>{language === 'pt' ? 'Componente extraordinária | Ajustamento não recorrente' : 'Extraordinary component | Non-recurring adjustment'}</p>
              </div>
            </div>
            <div className="space-y-2 text-xs leading-relaxed" style={{ color: '#344054' }}>
              <p>
                {language === 'pt' 
                  ? <>A provisão para existências de <strong style={{ color: '#9B7A00' }}>USD 2,0M</strong> resulta do <strong>write-down de inventários de baixa rotação</strong> identificados durante a revisão anual. Esta componente é de <strong>natureza não recorrente</strong> e não existia no exercício anterior, contribuindo para <strong>15% do saldo total de provisões</strong>.</>
                  : <>The inventory provision of <strong style={{ color: '#9B7A00' }}>USD 2.0M</strong> results from the <strong>write-down of slow-moving inventories</strong> identified during the annual review. This component is <strong>non-recurring in nature</strong> and did not exist in the prior year, contributing <strong>15% of total provisions balance</strong>.</>}
              </p>
              <div className="flex items-center gap-3 mt-3 pt-3" style={{ borderTop: '1px solid #EAD58A' }}>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#F5EBC4', color: '#9B7A00' }}>
                  {language === 'pt' ? 'EXTRAORDINÁRIO' : 'EXTRAORDINARY'}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#F5EBC4', color: '#9B7A00' }}>
                  {language === 'pt' ? 'NÃO RECORRENTE' : 'NON-RECURRING'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== */}
      {/* 6. RISCOS E ALERTAS DO BALANCO */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Riscos e Alertas do Balanco' : 'Balance Sheet Risks and Alerts'}
          </h3>
          <p className="text-sm text-slate-500">
            {language === 'pt' 
              ? 'Pontos de atencao que requerem monitorizacao ou accao de gestao' 
              : 'Attention points requiring monitoring or management action'}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {balanceAlerts[language].map((alert, idx) => (
            <AlertCard 
              key={idx}
              title={alert.title}
              description={alert.description}
              severity={alert.severity}
              icon={alert.icon}
            />
          ))}
        </div>
      </div>

      {/* ===================== */}
      {/* QUADRO DO BALANCO - 7 Columns with Sticky Header */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-serif text-lg font-semibold text-primary">
            {language === 'pt' ? 'Balanco Detalhado' : 'Detailed Balance Sheet'}
          </h3>
        </div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#0A192F] text-white">
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider w-[22%]">
                  {language === 'pt' ? 'Descricao' : 'Description'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider w-[11%]">
                  {year}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Orcamento' : 'Budget'} {year}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-amber-300 uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Var. Abs.' : 'Abs. Var.'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-amber-300 uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Var. %' : 'Var. %'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-[11%]">
                  {year - 1}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}
                </th>
              </tr>
            </thead>
            <tbody>
              {balanceSheetRows.map((row, idx) => {
                // Use REAL budget data from row.budget (from 2026 Budget Excel file)
                const budgetVal = row.budget !== undefined ? row.budget : 0;
                const varAbsVsBudget = (row.current || 0) - budgetVal;
                const varPctVsBudget = budgetVal !== 0 ? ((row.current - budgetVal) / Math.abs(budgetVal) * 100).toFixed(1) : '0';
                const varPctYoY = row.prev !== 0 && row.prev !== undefined ? ((row.current - row.prev) / Math.abs(row.prev) * 100).toFixed(1) : '0';
                
                // Main Header (ACTIVO, CAPITAL PROPRIO E PASSIVO)
                if (row.type === 'main-header') {
                  return (
                    <tr key={idx} className="bg-slate-100">
                      <td colSpan={7} className="py-3 px-4 font-bold text-primary text-sm">
                        {row.label[language]}
                      </td>
                    </tr>
                  );
                }
                
                // Sub Header (Activos nao Correntes, Capital Proprio, etc.)
                if (row.type === 'sub-header') {
                  return (
                    <tr key={idx} className="bg-slate-50">
                      <td colSpan={7} className="py-2 px-4 font-semibold text-slate-700 text-sm">
                        {row.label[language]}
                      </td>
                    </tr>
                  );
                }
                
                // Subtotal (Total Activos nao Correntes, Total Capital Proprio, etc.)
                if (row.type === 'subtotal') {
                  return (
                    <tr key={idx} className="bg-slate-50 font-semibold border-t border-slate-200">
                      <td className="py-2.5 px-4 text-slate-700">
                        {row.label[language]}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-slate-700">
                        {formatM(row.current)}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">
                        {formatM(budgetVal)}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        <span className={parseFloat(varAbsVsBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                          {varAbsVsBudget >= 0 ? '+' : ''}{formatM(varAbsVsBudget)}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span 
                          className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold"
                          style={{ 
                            backgroundColor: parseFloat(varPctVsBudget) >= 0 ? '#ECFDF5' : '#FEF2F2',
                            color: parseFloat(varPctVsBudget) >= 0 ? '#059669' : '#DC2626'
                          }}
                        >
                          {parseFloat(varPctVsBudget) >= 0 ? '+' : ''}{varPctVsBudget}%
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-slate-400">
                        {formatM(row.prev)}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span className={`text-xs font-medium ${parseFloat(varPctYoY) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {parseFloat(varPctYoY) >= 0 ? '+' : ''}{varPctYoY}%
                        </span>
                      </td>
                    </tr>
                  );
                }
                
                // Grand Total (Total do Activo, Total do Capital Proprio e Passivo)
                if (row.type === 'grand-total') {
                  return (
                    <tr key={idx} className="bg-gold-50/50 font-bold border-t-2 border-primary">
                      <td className="py-3 px-4 text-primary">
                        {row.label[language]}
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums text-primary">
                        {formatM(row.current)}
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums text-slate-500">
                        {formatM(budgetVal)}
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums">
                        <span className={parseFloat(varAbsVsBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                          {varAbsVsBudget >= 0 ? '+' : ''}{formatM(varAbsVsBudget)}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span 
                          className="inline-flex px-1.5 py-0.5 rounded text-xs font-bold"
                          style={{ 
                            backgroundColor: parseFloat(varPctVsBudget) >= 0 ? '#D1FAE5' : '#FEE2E2',
                            color: parseFloat(varPctVsBudget) >= 0 ? '#065F46' : '#991B1B'
                          }}
                        >
                          {parseFloat(varPctVsBudget) >= 0 ? '+' : ''}{varPctVsBudget}%
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums text-slate-500">
                        {formatM(row.prev)}
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className={`text-xs font-bold ${parseFloat(varPctYoY) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {parseFloat(varPctYoY) >= 0 ? '+' : ''}{varPctYoY}%
                        </span>
                      </td>
                    </tr>
                  );
                }
                
                // Regular row (indented line items)
                return (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className={`py-2 px-4 text-slate-600 ${row.indent ? 'pl-8' : ''}`}>
                      {row.label[language]}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-slate-700 font-medium">
                      {formatM(row.current)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-slate-500">
                      {formatM(budgetVal)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">
                      <span className={parseFloat(varAbsVsBudget) >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {varAbsVsBudget >= 0 ? '+' : ''}{formatM(varAbsVsBudget)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <span 
                        className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold"
                        style={{ 
                          backgroundColor: parseFloat(varPctVsBudget) >= 0 ? '#ECFDF5' : '#FEF2F2',
                          color: parseFloat(varPctVsBudget) >= 0 ? '#059669' : '#DC2626'
                        }}
                      >
                        {parseFloat(varPctVsBudget) >= 0 ? '+' : ''}{varPctVsBudget}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-slate-400">
                      {formatM(row.prev)}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <span className={`text-xs font-medium ${parseFloat(varPctYoY) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {parseFloat(varPctYoY) >= 0 ? '+' : ''}{varPctYoY}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BalanceSheet;
